<template>
  <Layout>
    <template #header-container>
      <div
        :class="[
          'mc-list-header',
          'mc-fleet-task-detail__header',
          { 'mc-list-header--sticky': isStickyHeader },
        ]"
        :style="mobileHeaderCssVars"
      >
        <PageHeader
          class="mc-fleet-task-detail__page-header"
          :title="title"
          subTitle=""
          :customTitle="isMobileHeader"
          :isSticky="!isMobileHeader || isStickyHeader"
        >
          <template v-if="isMobileHeader" #beforeTitle>
            <McButton
              class="mc-fleet-task-detail__leading-action"
              variant="icon-only"
              :icon="isCreating ? 'icon-outline_close' : 'icon-outline_arrow-left'"
              :aria-label="isCreating ? $t('global.cancel') : $t('global.back')"
              @click="isCreating ? handleCancel() : handleBack()"
            />
          </template>
          <template v-if="isMobileHeader" #title>
            <div class="mc-custom-page-title mc-fleet-task-detail__mobile-title">
              <h4 class="mc-page-title">{{ mobileHeaderTitle }}</h4>
              <p v-if="mobileHeaderNumber">{{ mobileHeaderNumber }}</p>
            </div>
          </template>
          <template v-if="!isLoadingInitial && !isNotEditable" #besideTitle>
            <div class="d-flex align-items-center gap-1 autosave-wrapper">
              <template v-if="isLoadingGlobal && !isInitial">
                <div class="mc-fa icon-outline_Sync" />
                <div>{{ $t('fleetTaskInstant.text.autosave.saving') }}</div>
              </template>
              <template v-else>
                <div id="autosave-tooltip" class="position-relative">
                  <svg class="circle-animation" height="18" width="18">
                    <circle cx="9" cy="9" r="7" />
                  </svg>
                  <div class="mc-fa icon-fill_checkmark-circle" />
                </div>
                <div v-if="!isInitial" class="saved-text">
                  {{ $t('fleetTaskInstant.text.autosave.saved') }}
                </div>
              </template>
              <b-tooltip
                v-if="!isLoadingGlobal"
                custom-class="autosave-tooltip-wrapper"
                target="autosave-tooltip"
                triggers="hover"
                placement="bottom"
              >
                <div>{{ $t('fleetTaskInstant.title.autosave') }}</div>
                <div class="subtitle">
                  {{ $t('fleetTaskInstant.subtitle.autosave', { time: showLastUpdated }) }}
                </div>
              </b-tooltip>
            </div>
          </template>
          <template v-if="!isMobileHeader" #afterTitle>
            <div class="d-flex align-items-center gap-2 mc-fleet-task-detail__header-actions">
              <template v-if="isCreating">
                <McButton
                  variant="secondary"
                  :text="$t('global.cancel')"
                  icon="icon-outline_close"
                  @click="handleCancel"
                />

                <McButton
                  variant="primary"
                  :text="$t('global.save')"
                  icon="icon-outline_save"
                  @click="handleTransitionStatus"
                />
              </template>

              <template v-else>
                <McButton
                  variant="secondary"
                  :text="$t('global.back')"
                  icon="icon-outline_arrow-left"
                  :screenBreakpoint="buttonScreenBreakpoint"
                  @click="handleBack"
                />
                <McButton
                  v-if="validateQuantityButtonState.visible"
                  variant="secondary"
                  :text="$t('fleetTask.action.validateQuantity')"
                  icon="icon-outline_cube"
                  :screenBreakpoint="buttonScreenBreakpoint"
                  :loading="showResponse.custom_field?.has_validate?.toLowerCase() === 'in progress'"
                  :disabled="validateQuantityButtonState.disabled"
                  @click="handleValidateQuantity"
                />
                <McButton
                  v-if="fleetTaskStatus === 'Perjalanan' && hasVendor === false"
                  variant="secondary"
                  icon="icon-outline_car"
                  :text="$t('tower.trackVehicles')"
                  :screenBreakpoint="buttonScreenBreakpoint"
                  @click="handleTrackFO"
                />
                <OtherOptions v-if="fleetTaskStatus != 'Temporary'" />
                <McTransitionStatus
                  v-if="!isTmsMarketing"
                  :isCreating="isCreating"
                  :entityData="showResponse"
                  :statusTransitions="statusTransitionsValue"
                  :disabled="isLoadingGlobal"
                  :alertTransition="showAlertTransition"
                  :getStatusLabelFn="getFleettaskStatusLabel"
                  @transition="handleTransitionStatus"
                />
                <McButton
                  v-if="
                    !isCreating &&
                    !hasVendor &&
                    ![
                      'Perjalanan',
                      'Selesai',
                      'Menunggu Vendor',
                      'Ditolak Vendor',
                      'Diterima Vendor',
                      'Dibatalkan',
                    ].includes(fleetTaskStatus) &&
                    !isTmsMarketing
                  "
                  :variant="buttonFleetTaskAutomation.variant"
                  :text="buttonFleetTaskAutomation.text"
                  :icon="buttonFleetTaskAutomation.icon"
                  :disabled="buttonFleetTaskAutomation.disabled"
                  :displayTooltip="buttonFleetTaskAutomation.displayTooltip"
                  :forceShowTooltip="buttonFleetTaskAutomation.forceShowTooltip"
                  tooltipPosition="bottom"
                  :tooltipText="buttonFleetTaskAutomation.tooltipText"
                  :tooltipWrap="true"
                  @click="handleTravelAutomation"
                />
                <McButton
                  v-if="fleetTaskStatus === 'Selesai'"
                  variant="secondary"
                  :text="$t('fleetTask.downloadPhoto.title')"
                  icon="icon-outline_download"
                  @click="handleDownloadPhoto"
                />
              </template>
            </div>
          </template>

          <template #footer>
            <div
              v-if="isMobileHeader"
              class="d-flex align-items-center gap-2 mc-fleet-task-detail__header-actions mc-fleet-task-detail__header-actions--mobile"
            >
              <template v-if="isCreating">
                <McButton
                  variant="primary"
                  :text="$t('global.save')"
                  icon="icon-outline_save"
                  @click="handleTransitionStatus"
                />
              </template>

              <template v-else>
                <McButton
                  v-if="fleetTaskStatus === 'Perjalanan' && hasVendor === false"
                  variant="icon-only"
                  icon="icon-outline_car"
                  :text="$t('tower.trackVehicles')"
                  tooltipPosition="bottom"
                  @click="handleTrackFO"
                />
                <OtherOptions v-if="fleetTaskStatus != 'Temporary'" />
                <McTransitionStatus
                  v-if="!isTmsMarketing"
                  :class="{
                    'mc-fleet-task-detail__transition-status-mobile':
                      fleetTaskStatus === 'Perjalanan',
                  }"
                  :isCreating="isCreating"
                  :entityData="showResponse"
                  :statusTransitions="statusTransitionsValue"
                  :disabled="isLoadingGlobal"
                  :alertTransition="showAlertTransition"
                  :getStatusLabelFn="getFleettaskStatusLabel"
                  @transition="handleTransitionStatus"
                />
                <McButton
                  v-if="
                    !isCreating &&
                    !hasVendor &&
                    ![
                      'Perjalanan',
                      'Selesai',
                      'Menunggu Vendor',
                      'Ditolak Vendor',
                      'Diterima Vendor',
                      'Dibatalkan',
                    ].includes(fleetTaskStatus) &&
                    !isTmsMarketing
                  "
                  class="mc-fleet-task-detail__travel-automation-button"
                  :variant="buttonFleetTaskAutomation.variant"
                  :text="buttonFleetTaskAutomation.text"
                  :icon="buttonFleetTaskAutomation.icon"
                  :disabled="buttonFleetTaskAutomation.disabled"
                  :displayTooltip="buttonFleetTaskAutomation.displayTooltip"
                  :forceShowTooltip="buttonFleetTaskAutomation.forceShowTooltip"
                  tooltipPosition="bottom"
                  :tooltipText="buttonFleetTaskAutomation.tooltipText"
                  :tooltipWrap="true"
                  @click="handleTravelAutomation"
                />
                <McButton
                  v-if="fleetTaskStatus === 'Selesai'"
                  variant="secondary"
                  :text="$t('fleetTask.downloadPhoto.title')"
                  icon="icon-outline_download"
                  @click="handleDownloadPhoto"
                />
              </template>
            </div>
            <b-skeleton-wrapper :loading="isLoadingInitial && !isAttachDelivery">
              <template #loading>
                <b-skeleton-img no-aspect height="30px" class="mc-wizard-skeleton mt-3" />
              </template>
              <McWizard
                v-if="isSendBySelf && !hasFeatureFleetTaskContractMGM"
                compact
                :items="wizardItems"
                :activeKey="fleetTaskStatus"
                class="mc-wizard mt-3"
              />
              <McWizard
                v-else-if="hasFeatureFleetTaskContractMGM"
                compact
                :items="wizardItemsMgm"
                :activeKey="fleetTaskStatus"
                class="mc-wizard mt-3"
              />
              <McWizard
                v-else
                compact
                :items="wizardItemsVendor"
                :activeKey="fleetTaskStatus"
                class="mc-wizard mt-3"
              />
            </b-skeleton-wrapper>
          </template>

          <template #banner>
            <div
              v-if="isUserGroupNull && ['Draf', 'Dijadwalkan', 'Ditolak'].includes(fleetTaskStatus)"
              class="mc-fo-banner warning"
            >
              <i class="mc-fo-banner__icon icon-fill_alert-triangle" />
              <div
                class="mc-fo-banner__text"
                v-html="$t('fleetTask.formV2.banner.userGroupRequired')"
              />
            </div>
            <div
              v-else-if="fleetTaskStatus === 'Diterima Vendor' && hasVendor"
              class="mc-fo-banner success"
            >
              <i class="mc-fo-banner__icon icon-fill_checkmark"></i>
              <div class="mc-fo-banner__text">
                {{ $t('fleetTask.formV2.banner.vendorAccepted') }}
              </div>
            </div>
            <div v-else-if="fleetTaskStatus === 'Ditolak' && hasVendor" class="mc-fo-banner failed">
              <i class="mc-fo-banner__icon icon-fill_close"></i>
              <div class="mc-fo-banner__text">
                {{ $t('fleetTask.formV2.banner.vendorRejected') }}
              </div>
            </div>
            <div
              v-else-if="showBannerAutomation"
              :class="`mc-fo-banner ${showBannerAutomation.variant}`"
            >
              <i :class="`mc-fo-banner__icon ${showBannerAutomation.icon}`"></i>
              <div class="mc-fo-banner__text" v-html="showBannerAutomation.message"></div>
              <div v-if="showBannerAutomation.isMulti" class="mc-fo-banner__see-all">
                <b-dropdown variant="link" toggle-tag="a" toggle-class="dropdown-link">
                  <template #button-content>
                    {{ $t('global.action.seeDetail') }}
                    <i class="chev icon-outline_chevron-up"></i>
                  </template>

                  <b-dropdown-item
                    v-for="(item, index) in showBannerAutomation.multiErrors"
                    :key="item.key || index"
                  >
                    <div class="d-flex align-items-start">
                      <McIconTooltip
                        id="isError"
                        :icon="'icon-fill_alert-circle'"
                        :showTooltip="false"
                        variant="danger"
                        placement="bottom"
                        class="align-self-start mc-fo-banner__item-icon"
                      />
                      <div class="d-flex flex-column">
                        <span class="mc-fo-banner__message">{{ item.message }}</span>
                        <small v-if="item.action" class="text-muted">{{ item.action }}</small>
                      </div>
                    </div>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
          </template>
        </PageHeader>
      </div>
    </template>

    <b-row>
      <b-col :md="!isFromVendorRequest && fleetTaskStatus !== 'Dibatalkan' ? 8 : 12">
        <FormulateForm
          ref="formRef"
          v-model="formValues"
          class="mc-form"
          name="detail"
          :errors="formErrors"
        >
          <div class="d-flex mc-list-header mt-3 mb-2">
            <h4 class="card-title flex-grow-1">
              {{ $t('fleetTask.detail') }}
            </h4>
          </div>
          
          <b-card 
            v-if="isSamator && ['Dijadwalkan', 'Perjalanan', 'Selesai'].includes(fleetTaskStatus)
              && scaleValue && isContainLiquid"
          >
            <TruckScaleTimeline
              :value="scaleValue"
              :title="$t('fleetTask.fields.truckScale')"
            />
          </b-card>

          <b-card>
            <b-col :xl="isFromVendorRequest ? 10 : 12">
              <b-row
                v-if="
                  !isFromVendorRequest &&
                  hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete'])
                "
                :class="hasFeatureFleetTaskContractMGM ? 'mb-3' : ''"
              >
                <b-col :md="hasFeatureFleetTaskContractMGM ? 6 : 8">
                  <b-row>
                    <p v-if="hasFeatureFleetTaskContractMGM" class="teal-300">
                      {{ $t('fleetTask.formV2.sendMethod.title') }}
                    </p>
                    <b-col md="6">
                      <div class="d-flex gap-2">
                        <McRadioButton
                          id="bySelf"
                          v-model="sendBy"
                          name="send_by"
                          :disabled="
                            isUserGroupNull ||
                            isLoadingGlobal ||
                            !['Draf', 'Temporary'].includes(fleetTaskStatus)
                          "
                          :initialValue="true"
                          :fullWidth="false"
                        />
                        <div class="ml-2">
                          <p class="fw-bold mb-2">{{ $t('fleetTask.formV2.sendMethod.self') }}</p>
                        </div>
                      </div>
                    </b-col>
                    <b-col md="6">
                      <div class="d-flex gap-2">
                        <McRadioButton
                          id="byVendor"
                          v-model="sendBy"
                          name="send_by"
                          :disabled="
                            isUserGroupNull ||
                            isLoadingGlobal ||
                            !['Draf', 'Temporary'].includes(fleetTaskStatus)
                          "
                          :initialValue="false"
                          :fullWidth="false"
                        />
                        <div class="ml-2">
                          <p class="fw-bold mb-2">{{ $t('fleetTask.formV2.sendMethod.vendor') }}</p>
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                </b-col>
                
                <b-col v-if="hasFeatureFleetTaskContractMGM && isSendBySelf" md="6">
                  <b-row>
                    <p class="teal-300">{{ $t('fleetTask.formV2.truckLoadType.title') }}</p>
                    <b-col md="6" class="d-flex align-items-center">
                      <p>
                        <strong>{{ truckLoadType.label }}</strong>
                      </p>
                      <McInfoTooltip
                        id="truk_load_type"
                        :tooltip="truckLoadType.tooltip"
                        class="ms-1"
                        style="margin-bottom: 10px"
                      />
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <b-row
                v-if="
                  hasFeatureFleetTaskContractMGM &&
                  !isFromVendorRequest &&
                  !hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete'])
                "
                style="margin-left: -5px"
              >
                <b-col md="6">
                  <b-row>
                    <p class="teal-300">{{ $t('fleetTask.formV2.truckLoadType.title') }}</p>
                    <b-col md="6" class="d-flex align-items-center">
                      <p>
                        <strong>{{ truckLoadType.label }}</strong>
                      </p>
                      <McInfoTooltip
                        id="truk_load_type"
                        :tooltip="truckLoadType.tooltip"
                        class="ms-1"
                        style="margin-bottom: 10px"
                      />
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <b-row v-if="isSendBySelf" class="g-2 __input-row">
                <b-col
                  v-for="field in fleetTaskDetailFields"
                  :key="field.name"
                  sm="12"
                  md="6"
                  class="g-2"
                >
                  <FormulateInput
                    v-bind="field"
                    :disabled="
                      isUserGroupNull ||
                      (isTmsMarketing && !['Temporary', 'Draf'].includes(fleetTaskStatus)) ||
                      fleetTaskStatus === 'Menunggu Persetujuan' ||
                      (field.name === 'end_address_id' &&
                      endIfAllDoDone &&
                      formValues?.end_address_id
                        ? true
                        : field.disabled ??
                          (isLoadingGlobal &&
                            (!(field.type === 'text' && focusedField) || isLoadingInitial))) ||
                      (field.name === 'bin_num' && isDisabledBinNum) ||
                      (field.name === 'contract_type' && isLessTruckLoad) ||
                      (field.name === 'fleet_task_type' &&
                        ['Disetujui', 'Dijadwalkan'].includes(fleetTaskStatus)) ||
                      (hasFeatureFleetTaskContractMGM &&
                        fleetTaskStatus === 'Draf' &&
                        [
                          'vehicle_type_id',
                          'vehicle_id',
                          'driver_user_id',
                          'driver_user_2_id',
                        ].includes(field.name) &&
                        !formValues?.vehicle_category_name)
                    "
                    :initialObject="field.getInitialObject?.(showResponse)"
                    :fullObject="fullObjects?.[field.name]"
                    :label="$t(field?.label)"
                    :placeholder="
                      !isLessTruckLoad &&
                      field.name !== 'contract_type' &&
                      formValues?.contract_type &&
                      fullObjects?.contract_type?.vehicle_available === false
                        ? $t('global.action.notAvailable')
                        : $t(field?.placeholder)
                    "
                    :help="
                      field?.validation?.includes('required')
                        ? $t('global.validation.required')
                        : ''
                    "
                    :class="[
                      'mc-fleet-task-header__input',
                      {
                        'mc-fleet-task-header__input--mobile-wide-datepicker':
                          field.name === 'expected_departure_on',
                      },
                    ]"
                    @input="field.type === 'text' ? handleTextInput($event, field.name) : null"
                    @blur-context="field.type === 'text' ? handleFieldBlur() : null"
                    @update:fullObject="handleFullObject({ key: field.name, value: $event })"
                  >
                    <template v-if="field.name === 'end_address_id'" #label>
                      <div
                        v-if="
                          ['Temporary', 'Draf', 'Dijadwalkan'].includes(fleetTaskStatus) &&
                          !isFromVendorRequest
                        "
                      >
                        <div
                          class="d-flex justify-content-between align-items-center"
                          style="margin-bottom: -3px"
                        >
                          <p class="text-muted mb-2">{{ $t('fleetTask.sequence.endPoint') }}</p>
                          <div
                            class="form-check form-switch form-switch-sm d-flex align-items-center"
                            style="margin-bottom: 7px"
                          >
                            <input
                              class="form-check-input me-2"
                              type="checkbox"
                              style="margin-bottom: 7px"
                              :checked="endIfAllDoDone"
                              :disabled="
                                isUserGroupNull ||
                                (isTmsMarketing &&
                                  !['Temporary', 'Draf'].includes(fleetTaskStatus)) ||
                                fleetTaskStatus === 'Menunggu Persetujuan' ||
                                !hasDeliveryOrder
                              "
                              @change="handleChangeEndIfAllDoDone"
                            />
                            <span
                              class="text-muted d-flex align-items-center"
                              style="margin-bottom: 4px"
                            >
                              {{ $t('fleetTask.formV2.endPoint.endIfAllDoDone') }}
                              <McInfoTooltip
                                id="set_end_if_all_do_done"
                                :tooltip="
                                  !hasDeliveryOrder
                                    ? $t('fleetTask.formV2.endPoint.disabledTooltip')
                                    : $t('fleetTask.formV2.endPoint.enabledTooltip')
                                "
                                class="ms-1"
                              />
                            </span>
                          </div>
                        </div>
                      </div>

                      <div
                        v-else-if="
                          endIfAllDoDone && ['Perjalanan', 'Selesai'].includes(fleetTaskStatus)
                        "
                        class="d-flex align-items-center"
                      >
                        <p class="text-muted mb-2 gap-2">{{ $t('fleetTask.sequence.endPoint') }}</p>
                        <McBadge
                          :text="$t('fleetTask.formV2.endPoint.endAfterDoDone')"
                          variant="outlinePrimary"
                          size="medium"
                          type="button"
                          style="margin-left: 10px; margin-bottom: 5px"
                        />
                      </div>
                    </template>
                    <template v-if="isNotEditable" #element="{ slotProps, attributes, model }">
                      <div v-if="field.type == 'date-picker'" class="fw-bold mc-title">
                        {{ formatDateWithTz(model, attributes.format) }}
                      </div>
                      <div
                        v-else-if="field.name === 'vehicle_id'"
                        class="d-flex align-items-center gap-2"
                      >
                        <div class="fw-bold mc-title">
                          {{
                            slotProps.component.fullObject?.license_plate ||
                            slotProps.component.initialObject?.license_plate ||
                            model ||
                            '-'
                          }}
                        </div>
                        <McBadge
                          v-if="
                            slotProps.component.fullObject?.hull_no ||
                            slotProps.component.initialObject?.hull_no
                          "
                          :text="
                            slotProps.component.fullObject?.hull_no ||
                            slotProps.component.initialObject?.hull_no
                          "
                          variant="outlinePlainSecondary"
                          size="medium"
                          type="button"
                        />
                      </div>
                      <div
                        v-else-if="
                          slotProps?.component?.initialObject &&
                          typeof slotProps.component.initialObject === 'object' &&
                          slotProps.component.initialObject[field.displayedBy]
                        "
                        class="fw-bold mc-title"
                      >
                        {{ slotProps.component.initialObject[field.displayedBy] }}
                      </div>
                      <div
                        v-else-if="
                          slotProps?.component?.fullObject &&
                          typeof slotProps.component.fullObject === 'object' &&
                          slotProps.component.fullObject[field.displayedBy]
                        "
                        class="fw-bold mc-title"
                      >
                        {{ slotProps.component.fullObject[field.displayedBy] }}
                      </div>
                      <div v-else-if="field.name === 'box_type'">
                        <p class="text-muted mc-fleet-task-box-type">{{ field.title }}</p>
                        {{ slotProps.component.result }}
                      </div>
                      <div v-else>
                        {{ isEmpty(model) ? '-' : model }}
                      </div>
                    </template>
                    <template v-if="isNotEditable" #help>
                      <span></span>
                    </template>
                  </FormulateInput>
                  <template
                    v-if="
                      (field.name === 'start_address_id' || field.name === 'end_address_id') &&
                      isCreating
                    "
                  >
                    <b-form-group class="mt-1">
                      <input
                        v-if="field.name === 'start_address_id'"
                        v-model="saveAsDefaultStart"
                        class="form-check-input me-2 custom-control-input"
                        type="checkbox"
                        :disabled="disableDefaultStart"
                      />
                      <input
                        v-else
                        v-model="saveAsDefaultEnd"
                        class="form-check-input me-2 custom-control-input"
                        type="checkbox"
                        :disabled="disableDefaultEnd"
                      />
                      <label>{{ $t('global.action.saveAsDefault') }}</label>
                    </b-form-group>
                  </template>
                  <template
                    v-if="
                      field.name === 'expected_departure_on' &&
                      ['Temporary', 'Draf', 'Dijadwalkan'].includes(fleetTaskStatus) &&
                      isEstimatedOutsideOperationalTime
                    "
                  >
                    <div class="mc-fleet-fask-operational-day">
                      <i class="mc-fa icon-fill_alert-triangle"></i>
                      <p>{{ $t('fleetTask.formV2.alerts.outsideOperationalTime') }}</p>
                    </div>
                  </template>
                </b-col>

                <template>
                  <hr v-if="hasFeatureFleetTaskContractMGM" />
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h4 v-if="hasFeatureFleetTaskContractMGM" class="card-title mb-0">
                      {{ $t('fleetTask.formV2.vehicleAndDriver.title') }}
                    </h4>
                    <div v-if="canEditVehicleSection" class="d-flex align-items-center gap-2">
                      <McButton
                        v-if="!isEditingVehicleSection"
                        variant="secondary"
                        :text="$t('global.action.edit')"
                        icon="icon-outline_edit"
                        :disabled="isLoadingGlobal"
                        @click="handleVehicleSectionEdit"
                      />
                      <template v-else>
                        <McButton
                          variant="secondary"
                          :text="$t('global.cancel')"
                          icon="icon-outline_close"
                          :disabled="isLoadingGlobal"
                          @click="handleVehicleSectionCancel"
                        />
                        <McButton
                          variant="primary"
                          :text="$t('global.save')"
                          icon="icon-outline_save"
                          :disabled="isLoadingGlobal"
                          @click="handleVehicleSectionSave"
                        />
                      </template>
                    </div>
                  </div>
                  <b-row>
                    <McAlert
                      v-if="
                        hasFeatureFleetTaskContractMGM &&
                        !fullObjects?.contract_type?.vehicle_available &&
                        formValues?.contract_type
                      "
                      class="ms-2"
                      variant="warning"
                      :html="$t('fleetTask.formV2.alerts.vehicleAndDriverUnavailable')"
                      withIcon
                      :withGap="false"
                    />
                  </b-row>
                  <b-col
                    v-for="field in vehicleRelatedFields"
                    :key="field.name"
                    sm="12"
                    md="6"
                    class="g-2"
                  >
                    <FormulateInput
                      v-bind="field"
                      :disabled="
                        isUserGroupNull ||
                        (isTmsMarketing && !['Temporary', 'Draf'].includes(fleetTaskStatus)) ||
                        fleetTaskStatus === 'Menunggu Persetujuan' ||
                        (field.name === 'end_address_id' &&
                        endIfAllDoDone &&
                        formValues?.end_address_id
                          ? true
                          : field.disabled ??
                            (isLoadingGlobal &&
                              (!(field.type === 'text' && focusedField) || isLoadingInitial))) ||
                        (field.name === 'bin_num' && isDisabledBinNum) ||
                        (field.name === 'contract_type' && isLessTruckLoad) ||
                        (!isLessTruckLoad &&
                          ((field.name === 'vehicle_category_name' &&
                            (!formValues?.contract_type ||
                              fullObjects?.contract_type?.vehicle_available === false)) ||
                            ([
                              'vehicle_type_id',
                              'vehicle_id',
                              'driver_user_id',
                              'driver_user_2_id',
                            ].includes(field.name) &&
                              !formValues?.vehicle_category_name))) ||
                        (field.name === 'fleet_task_type' &&
                          ['Disetujui', 'Dijadwalkan'].includes(fleetTaskStatus)) ||
                        isVehicleSectionFieldDisabled(field.name) ||
                        (hasFeatureFleetTaskContractMGM &&
                          fleetTaskStatus === 'Draf' &&
                          [
                            'vehicle_type_id',
                            'vehicle_id',
                            'driver_user_id',
                            'driver_user_2_id',
                          ].includes(field.name) &&
                          !formValues?.vehicle_category_name)
                      "
                      :initialObject="field.getInitialObject?.(showResponse)"
                      :fullObject="fullObjects?.[field.name]"
                      :label="$t(field?.label)"
                      :placeholder="
                        !isLessTruckLoad &&
                        field.name !== 'contract_type' &&
                        formValues?.contract_type &&
                        fullObjects?.contract_type?.vehicle_available === false
                          ? $t('global.action.notAvailable')
                          : $t(field?.placeholder)
                      "
                      :help="
                        field?.validation?.includes('required')
                          ? $t('global.validation.required')
                          : ''
                      "
                      :class="[
                        'mc-fleet-task-header__input',
                        {
                          'mc-fleet-task-header__input--mobile-wide-datepicker':
                            field.name === 'expected_departure_on',
                        },
                      ]"
                      @input="field.type === 'text' ? handleTextInput($event, field.name) : null"
                      @blur-context="field.type === 'text' ? handleFieldBlur() : null"
                      @update:fullObject="handleFullObject({ key: field.name, value: $event })"
                    >
                      <template v-if="field.name === 'end_address_id'" #label>
                        <div
                          v-if="
                            ['Temporary', 'Draf', 'Dijadwalkan'].includes(fleetTaskStatus) &&
                            !isFromVendorRequest
                          "
                        >
                          <div
                            class="d-flex justify-content-between align-items-center"
                            style="margin-bottom: -3px"
                          >
                            <p class="text-muted mb-2">{{ $t('fleetTask.sequence.endPoint') }}</p>
                            <div
                              class="form-check form-switch form-switch-sm d-flex align-items-center"
                              style="margin-bottom: 7px"
                            >
                              <input
                                class="form-check-input me-2"
                                type="checkbox"
                                style="margin-bottom: 7px"
                                :checked="endIfAllDoDone"
                                :disabled="
                                  isUserGroupNull ||
                                  (isTmsMarketing &&
                                    !['Temporary', 'Draf'].includes(fleetTaskStatus)) ||
                                  fleetTaskStatus === 'Menunggu Persetujuan' ||
                                  !hasDeliveryOrder
                                "
                                @change="handleChangeEndIfAllDoDone"
                              />
                              <span
                                class="text-muted d-flex align-items-center"
                                style="margin-bottom: 4px"
                              >
                                {{ $t('fleetTask.formV2.endPoint.endIfAllDoDone') }}
                                <McInfoTooltip
                                  id="set_end_if_all_do_done"
                                  :tooltip="
                                    !hasDeliveryOrder
                                      ? $t('fleetTask.formV2.endPoint.disabledTooltip')
                                      : $t('fleetTask.formV2.endPoint.enabledTooltip')
                                  "
                                  class="ms-1"
                                />
                              </span>
                            </div>
                          </div>
                        </div>

                        <div
                          v-else-if="
                            endIfAllDoDone && ['Perjalanan', 'Selesai'].includes(fleetTaskStatus)
                          "
                          class="d-flex align-items-center"
                        >
                          <p class="text-muted mb-2 gap-2">
                            {{ $t('fleetTask.sequence.endPoint') }}
                          </p>
                          <McBadge
                            :text="$t('fleetTask.formV2.endPoint.endAfterDoDone')"
                            variant="outlinePrimary"
                            size="medium"
                            type="button"
                            style="margin-left: 10px; margin-bottom: 5px"
                          />
                        </div>
                      </template>
                      <template v-if="isNotEditable" #element="{ slotProps, attributes, model }">
                        <div v-if="field.type == 'date-picker'" class="fw-bold mc-title">
                          {{ formatDateWithTz(model, attributes.format) }}
                        </div>
                        <div
                          v-else-if="field.name === 'vehicle_id'"
                          class="d-flex align-items-center gap-2"
                        >
                          <div class="fw-bold mc-title">
                            {{
                              slotProps.component.fullObject?.license_plate ||
                              slotProps.component.initialObject?.license_plate ||
                              model ||
                              '-'
                            }}
                          </div>
                          <McBadge
                            v-if="
                              slotProps.component.fullObject?.hull_no ||
                              slotProps.component.initialObject?.hull_no
                            "
                            :text="
                              slotProps.component.fullObject?.hull_no ||
                              slotProps.component.initialObject?.hull_no
                            "
                            variant="outlinePlain"
                            size="medium"
                            type="button"
                          />
                        </div>
                        <div
                          v-else-if="
                            slotProps?.component?.initialObject &&
                            typeof slotProps.component.initialObject === 'object' &&
                            slotProps.component.initialObject[field.displayedBy]
                          "
                          class="fw-bold mc-title"
                        >
                          {{ slotProps.component.initialObject[field.displayedBy] }}
                        </div>
                        <div
                          v-else-if="
                            slotProps?.component?.fullObject &&
                            typeof slotProps.component.fullObject === 'object' &&
                            slotProps.component.fullObject[field.displayedBy]
                          "
                          class="fw-bold mc-title"
                        >
                          {{ slotProps.component.fullObject[field.displayedBy] }}
                        </div>
                        <div v-else-if="field.name === 'box_type'">
                          <p class="text-muted mc-fleet-task-box-type">{{ field.title }}</p>
                          {{ slotProps.component.result }}
                        </div>
                        <div v-else>
                          {{ isEmpty(model) ? '-' : model }}
                        </div>
                      </template>
                      <template v-if="isNotEditable" #help>
                        <span></span>
                      </template>
                    </FormulateInput>
                    <template
                      v-if="
                        (field.name === 'start_address_id' || field.name === 'end_address_id') &&
                        isCreating
                      "
                    >
                      <b-form-group class="mt-1">
                        <input
                          v-if="field.name === 'start_address_id'"
                          v-model="saveAsDefaultStart"
                          class="form-check-input me-2 custom-control-input"
                          type="checkbox"
                          :disabled="disableDefaultStart"
                        />
                        <input
                          v-else
                          v-model="saveAsDefaultEnd"
                          class="form-check-input me-2 custom-control-input"
                          type="checkbox"
                          :disabled="disableDefaultEnd"
                        />
                        <label>{{ $t('global.action.saveAsDefault') }}</label>
                      </b-form-group>
                    </template>
                    <template
                      v-if="
                        field.name === 'expected_departure_on' &&
                        ['Temporary', 'Draf', 'Dijadwalkan'].includes(fleetTaskStatus) &&
                        isEstimatedOutsideOperationalTime
                      "
                    >
                      <div class="mc-fleet-fask-operational-day">
                        <i class="mc-fa icon-fill_alert-triangle"></i>
                        <p>{{ $t('fleetTask.formV2.alerts.outsideOperationalTime') }}</p>
                      </div>
                    </template>
                  </b-col>
                </template>
                <b-col
                  v-if="hasAccessAutocomplete && isNotEditable && isAutoCompleteOn"
                  sm="12"
                  md="4"
                  class="g-2"
                >
                  <div class="p-1">
                    <p class="mb-2">{{ $t('fleetTask.formV2.deliveryStatus.title') }}</p>
                    <div class="d-flex align-items-center gap-2">
                      <p class="fw-bold mc-title mb-0">
                        {{ $t('fleetTask.formV2.deliveryStatus.automatic') }}
                      </p>
                      <p class="mc-title mb-0">
                        {{ $t('fleetTask.formV2.deliveryStatus.automaticSource') }}
                      </p>
                      <McInfoTooltip
                        v-if="isOverlap && fleetTaskStatus === 'Perjalanan'"
                        id="multi_customer"
                        :tooltip="$t('fleetTask.formV2.deliveryStatus.automaticOverlapTooltip')"
                        iconType="fill"
                        variant="danger"
                      />
                    </div>
                  </div>
                </b-col>
                <b-col v-else-if="isNotEditable" sm="12" md="6" class="g-2">
                  <div class="p-1">
                    <p class="mb-2">{{ $t('fleetTask.formV2.deliveryStatus.title') }}</p>
                    <div class="d-flex align-items-center gap-2">
                      <p class="fw-bold mc-title mb-0">
                        {{ $t('fleetTask.formV2.deliveryStatus.manualWithSource') }}
                      </p>
                    </div>
                  </div>
                </b-col>
              </b-row>
              <b-row v-else class="g-2 __input-row">
                <b-col v-for="field in vendorField" :key="field.name" sm="12" md="6" class="g-2">
                  <FormulateInput
                    v-bind="field"
                    :disabled="
                      field.disabled ??
                      (isLoadingGlobal ||
                        (fleetTaskStatus !== 'Draf' &&
                          fleetTaskStatus !== 'Temporary' &&
                          fleetTaskStatus !== 'Ditolak'))
                    "
                    :fullObject="fullObjects?.[field.name]"
                    :class="[
                      'mc-fleet-task-header__input',
                      {
                        'mc-fleet-task-header__input--mobile-wide-datepicker':
                          field.name === 'expected_departure_on',
                      },
                    ]"
                  />
                </b-col>
                <b-row v-if="['Dijadwalkan', 'Perjalanan', 'Selesai'].includes(fleetTaskStatus)">
                  <b-col
                    v-for="field in fleetTaskDetailFields"
                    :key="field.name"
                    sm="12"
                    md="4"
                    class="g-2"
                  >
                    <FormulateInput
                      v-bind="field"
                      :disabled="field.disabled ?? isLoadingGlobal"
                      :initialObject="field.getInitialObject?.(showResponse)"
                      :fullObject="fullObjects?.[field.name]"
                      :help="field?.validation?.includes('required')"
                      :label="$t(field?.label)"
                      :class="[
                        'mc-fleet-task-header__input',
                        {
                          'mc-fleet-task-header__input--mobile-wide-datepicker':
                            field.name === 'expected_departure_on',
                        },
                      ]"
                    >
                      <template v-if="isNotEditable" #element="{ slotProps, attributes, model }">
                        <div v-if="field.type == 'date-picker'" class="fw-bold mc-title">
                          {{ formatDateWithTz(model, attributes.format) }}
                        </div>
                        <div
                          v-else-if="field.name === 'vehicle_id'"
                          class="d-flex align-items-center gap-2"
                        >
                          <div class="fw-bold mc-title">
                            {{
                              slotProps.component.fullObject?.license_plate ||
                              slotProps.component.initialObject?.license_plate ||
                              model ||
                              '-'
                            }}
                          </div>
                          <McBadge
                            v-if="
                              slotProps.component.fullObject?.hull_no ||
                              slotProps.component.initialObject?.hull_no
                            "
                            :text="
                              slotProps.component.fullObject?.hull_no ||
                              slotProps.component.initialObject?.hull_no
                            "
                            variant="outlinePlain"
                            size="medium"
                            type="button"
                          />
                        </div>
                        <div
                          v-else-if="
                            slotProps?.component?.initialObject &&
                            typeof slotProps.component.initialObject === 'object' &&
                            slotProps.component.initialObject[field.displayedBy]
                          "
                          class="fw-bold mc-title"
                        >
                          {{ slotProps.component.initialObject[field.displayedBy] }}
                        </div>
                        <div
                          v-else-if="
                            slotProps?.component?.fullObject &&
                            typeof slotProps.component.fullObject === 'object' &&
                            slotProps.component.fullObject[field.displayedBy]
                          "
                          class="fw-bold mc-title"
                        >
                          {{ slotProps.component.fullObject[field.displayedBy] }}
                        </div>
                        <div v-else>
                          {{ isEmpty(model) ? '-' : model }}
                        </div>
                      </template>
                      <template v-if="isNotEditable" #help>
                        <span></span>
                      </template>
                    </FormulateInput>
                  </b-col>
                </b-row>
              </b-row>
            </b-col>
            <template v-if="hasAccessAutocomplete && !isNotEditable && isSendBySelf">
              <hr />
              <b-col :xl="isFromVendorRequest ? 10 : 12">
                <h4 class="card-title mb-3">{{ $t('fleetTask.formV2.deliveryStatus.title') }}</h4>
                <b-row>
                  <b-col md="6">
                    <div class="d-flex gap-2">
                      <McRadioButton
                        id="manual"
                        v-model="isAutoCompleteOn"
                        name="is_autocomplete_on"
                        :disabled="
                          isUserGroupNull ||
                          isLoadingGlobal ||
                          (isTmsMarketing && !['Temporary', 'Draf'].includes(fleetTaskStatus)) ||
                          fleetTaskStatus === 'Menunggu Persetujuan'
                        "
                        :initialValue="false"
                        :fullWidth="false"
                      />
                      <div class="ml-2">
                        <div class="d-flex align-items-center gap-2">
                          <p class="fw-bold mc-title mb-0">
                            {{ $t('fleetTask.formV2.deliveryStatus.manual') }}
                          </p>
                        </div>
                        <p>{{ $t('fleetTask.formV2.deliveryStatus.manualDescription') }}</p>
                      </div>
                    </div>
                  </b-col>
                  <b-col md="6">
                    <div class="d-flex gap-2">
                      <McRadioButton
                        id="auto"
                        v-model="isAutoCompleteOn"
                        name="is_autocomplete_on"
                        :disabled="
                          isUserGroupNull ||
                          isLoadingGlobal ||
                          (isTmsMarketing && !['Temporary', 'Draf'].includes(fleetTaskStatus)) ||
                          fleetTaskStatus === 'Menunggu Persetujuan'
                        "
                        :initialValue="true"
                        :fullWidth="false"
                      />
                      <div class="ml-2">
                        <div class="d-flex align-items-center gap-2">
                          <p class="fw-bold mc-title mb-0">
                            {{ $t('fleetTask.formV2.deliveryStatus.automatic') }}
                          </p>
                        </div>
                        <p>{{ $t('fleetTask.formV2.deliveryStatus.automaticDescription') }}</p>
                        <Multiselect
                          v-model="autoCompleteSource"
                          track-by="value"
                          label="label"
                          :options="autoCompleteOptions"
                          disabled
                        >
                          <template #option="{ option }">
                            <div class="d-flex justify-content-between">
                              <span>{{ option.value }}</span>
                              <span
                                v-if="
                                  option.isDefault && autoCompleteSource?.value !== option.value
                                "
                                >{{ $t('global.default') }}
                              </span>
                            </div>
                          </template>
                        </Multiselect>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </b-col>
            </template>
          </b-card>
        </FormulateForm>
      </b-col>
      <b-col md="4">
        <div v-if="!isFromVendorRequest && fleetTaskStatus !== 'Dibatalkan'">
          <Notification
            :hasErrorNotification="hasErrorNotification"
            :isUserGroupNull="isUserGroupNull"
            :disabled="
              isLoadingGlobal ||
              (isTmsMarketing && !['Temporary', 'Draf'].includes(fleetTaskStatus)) ||
              fleetTaskStatus === 'Menunggu Persetujuan'
            "
            @notification="handleNotificationChange"
            @customerPhoneNumber="handleCustomerPhoneNumberChange"
          />
        </div>

        <div v-if="hasFeatureFleetTaskContractMGM">
          <h4 class="card-title flex-grow-1">
            {{ $t('fleetTask.formV2.attachment.title') }}
          </h4>
          <b-card>
            <FormulateForm ref="attachmentForm" name="attachment">
              <FormulateInput
                type="file-uploader-custom"
                name="dokumen"
                v-model="attachmentFiles"
                :label="$t('fleetTask.formV2.attachment.uploadLabel')"
                :max-size="2"
                :allowed-formats="['.jpeg', '.jpg', '.png', '.pdf', '.docx']"
                :show-allowed-formats="
                  !isLessTruckLoad && ['Temporary', 'Draf'].includes(fleetTaskStatus)
                "
                :multiple="false"
                :required="isFullTruckLoadImmediate && ['Draf'].includes(fleetTaskStatus)"
                :readonly="!['Temporary', 'Draf'].includes(fleetTaskStatus)"
                :disabled="!!isLessTruckLoad"
                :validation="
                  isFullTruckLoadImmediate && ['Draf'].includes(fleetTaskStatus) ? 'required' : ''
                "
                @file-selected="handleFileChange"
                @file-removed="handleFileRemoved"
              />
            </FormulateForm>
          </b-card>
        </div>
      </b-col>
    </b-row>

    <div>
      <div class="d-flex mc-list-header mt-3 mb-2 align-items-center">
        <h4 class="card-title flex-grow-1">
          {{ $t('fleetTask.orderTask') }}
        </h4>

        <McButton
          v-if="showBulkTransferButton"
          icon="icon-outline_swap"
          :text="$t('fleetTask.formV2.action.transferDelivery')"
          :screenBreakpoint="buttonScreenBreakpoint"
          variant="secondary"
          class="me-2"
          :disabled="
            isUserGroupNull || (isTmsMarketing && !['Temporary', 'Draf'].includes(fleetTaskStatus))
          "
          @click="handleBulkTransfer"
        />

        <McButton
          v-if="showAddDeliveryButton"
          icon="icon-fill_plus"
          :text="$t('fleetTask.addDelivery')"
          :screenBreakpoint="buttonScreenBreakpoint"
          :disabled="
            isUserGroupNull ||
            (isTmsMarketing && !['Temporary', 'Draf'].includes(fleetTaskStatus)) ||
            fleetTaskStatus === 'Menunggu Persetujuan'
          "
          @click="handleAttachDelivery"
        />
      </div>

      <b-card>
        <b-row class="g-0 mb-3 mc-total">
          <b-col sm="12" md="2" class="mc-total__item">
            <div class="mc-total__label">
              <span class="gap-2">
                <i class="icon-fill_weight"></i>
              </span>
              {{ $t('fleetTask.fields.totalWeight') }}
            </div>
            <div
              v-if="!isLoadingGlobal"
              class="mc-total__value fw-bold d-flex justify-content-between"
            >
              <div>
                {{ totalWeight }} / {{ totalWeightCapacity }} Kg
                <span :style="totalWeightPercentageStyle"> ({{ totalWeightPercentage }}%) </span>
              </div>
            </div>
            <div v-else class="mc-total__value">...</div>
          </b-col>

          <b-col sm="12" md="2" class="mc-total__item">
            <div class="mc-total__label">
              <span class="gap-2">
                <i class="icon-fill_cube"></i>
              </span>
              {{ $t('fleetTask.fields.totalVolume') }}
            </div>
            <div
              v-if="!isLoadingGlobal"
              class="mc-total__value fw-bold d-flex justify-content-between"
            >
              <div>
                {{ totalVolume }} / {{ totalVolumeCapacity }} Cbm
                <span :style="totalVolumePercentageStyle"> ({{ totalVolumePercentage }}%) </span>
              </div>
            </div>
            <div v-else class="mc-total__value">...</div>
          </b-col>

          <b-col
            v-if="hasFeatureFleetTaskContractMGM && hasFeatureBufferTimeMGM"
            sm="12"
            md="3"
            class="mc-total__item"
          >
            <div class="mc-total__label">
              {{ $t('fleetTask.fields.additionalDuration') }}
            </div>
            <div
              v-if="!isLoadingGlobal && buffer"
              class="mc-total__value fw-bold d-flex justify-content-between"
            >
              <div>
                {{ formatISODuration(buffer.duration) }}
                <span> ({{ toHour(buffer.start_time) }} - {{ toHour(buffer.end_time) }}) </span>
                <span v-if="buffer.day_offset > 0">
                  <McBadge
                    :text="`+${String(buffer.day_offset || 0)} ${$t('global.time.day')}`"
                    variant="outlineWarning"
                    size="medium"
                    type="button"
                  />
                </span>
              </div>
            </div>
            <div v-else class="mc-total__value">...</div>
          </b-col>

          <b-col v-if="hasTmsUserGroupAccess" sm="12" md="3" class="mc-total__item">
            <div class="mc-total__label">{{ $t('global.field.userGroup') }}</div>
            <template
              v-if="
                !showResponse?.user_group &&
                ['Draf', 'Dijadwalkan', 'Ditolak'].includes(fleetTaskStatus)
              "
            >
              <div class="mc-total__value">
                <FormulateForm
                  v-model="formValues"
                  class="mc-form"
                  name="userGroupForm"
                  :errors="formErrors"
                >
                  <FormulateInput
                    v-bind="userGroupField"
                    :disabled="
                      isLoadingGlobal ||
                      (isTmsMarketing && !['Temporary', 'Draf'].includes(fleetTaskStatus)) ||
                      fleetTaskStatus === 'Menunggu Persetujuan'
                    "
                  />
                </FormulateForm>
              </div>
            </template>
            <!-- Show text when user_group exists -->
            <template v-else>
              <div v-if="!isLoadingGlobal" class="mc-total__value fw-bold">
                {{ responseUserGroup }}
              </div>
              <div v-else class="mc-total__value">...</div>
            </template>
          </b-col>
        </b-row>

        <Sequence
          :sequence="sequence"
          :sequenceActLoads="sequenceActLoads"
          :missedRequiredFields="missedSequencingRequiredFields"
          :isLoading="isLoadingGlobal"
          :hasTmsUserGroupAccess="hasTmsUserGroupAccess"
          :isUserGroupNull="isUserGroupNull"
          :isTmsMarketing="isTmsMarketing"
        />
      </b-card>
    </div>

    <b-modal
      id="mc-modal-back"
      ref="mc-modal-back"
      class="mc-modal"
      :title="$t('global.confirmation.title')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>{{ $t('global.confirmation.question') }}</p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex mx-0">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.confirmation.exitPage')"
            @click="handleCancel"
          />
          <McButton
            variant="primary"
            :text="$t('global.confirmation.stayPage')"
            @click="cancel()"
          />
        </div>
      </template>
    </b-modal>
    <ModalChangeSeqVendor @change-vendor="handleChangeVendor" />
    <ModalShare :detailData="dataFO" />
    <ModalConfirmAutomation
      :foNumber="showResponse?.number"
      :status="fleetTaskStatus"
      :isSameOriginAndFirstDoPickup="sameOriginAndFirstDoPickup"
      @set-automation="handleSetAutomation"
    />
    <ModalDeactivatedAutomation
      :foNumber="showResponse?.number"
      @deactivated-automation="handleDeactivatedtAutomation"
    />
    <TransferDeliveryOrderModal :isBulkMode="true" :deliveryOrderIds="transferableOrderIds" />
    <DownloadAttachmentModal :fleetTaskUuid="$route.params.id" />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import { MixPanel } from '@/mixin/mixpanel'
import { Helper } from '@/mixin/helper'

import PageHeader from '@/components/page-header-v2'
import McWizard from '@/components/McWizard'
import McButton from '@/components/McButton'
import McRadioButton from '@/components/McRadioButton'
import Sequence from './components/Sequence'
import McTransitionStatus from '@/components/McTransitionStatus'
import DownloadAttachmentModal from './components/Modal/DownloadAttachment'
import McInfoTooltip from '@/components/McInfoTooltip'
import McBadge from '@/components/McBadge'
import McIconTooltip from '@/components/McIconTooltip'
import McAlert from '@/components/McAlert'
import OtherOptions from './components/OtherOptions'
import ModalChangeSeqVendor from './components/Modal/ChangeSeqVendor'
import ModalShare from './components/Modal/Share'
import Notification from './components/Notification'
import ModalConfirmAutomation from './components/Modal/ConfirmAutomation'
import ModalDeactivatedAutomation from './components/Modal/DeactivatedAutomation'
import TransferDeliveryOrderModal from './components/TransferDeliveryOrderModal'
import TruckScaleTimeline from './components/TruckScaleTimeline'
import Multiselect from 'vue-multiselect'

import { mapState, mapGetters } from 'vuex'
import {
  temporaryFleetTaskDetailFields,
  draftFleetTaskDetailFields,
  vendorFleetTaskDetailFields,
  userGroupField,
  setFleetTaskSelectedVehicleCategory,
  setFleetTaskSelectedDriverUser2Id,
  setFleetTaskSelectedDriverUserId,
  setFleetTaskSelectedVehicleId,
  setFleetTaskSelectedVehicleTypeId,
  setFleetTaskCustomerContractType,
} from './lib/fields/fleetTaskDetailFields'
import { getValidateQuantityButtonState } from './lib/helpers'
import { EventBus } from './lib/event-bus'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import { formatDateWithTz, formatIndexDate } from '@/helpers/tzDate'
import { formatOrDash } from '@/helpers/format-number.js'

import moment from 'moment'
moment.locale('id')

import {
  wizardStatusItems,
  wizardStatusItemsVendor,
  wizardStatusItemsMgm,
} from './lib/constants/wizard-status-items'
import { statusIdToLabelMap, statusNameToIdMap } from './lib/constants/status'
import statusTransitions from './lib/constants/status-transitions'
import toastErrors from './lib/constants/toast-errors'
import apiErrorMappings from './lib/constants/api-error-mappings'
import actionErrorMappings from './lib/constants/action-error-mappings'
import autoCompleteOptions from './lib/constants/auto-complete-options'
import { transitionError } from './lib/constants/error-message-mappings'
import { errorCodeToastMap, errorCodeInputMap } from './lib/constants/error-code-mappings'

import * as FleetTask from '@/services/rest-api/lib/fleet-task/fleet-task'
import * as FleetTaskPoint from '@/services/rest-api/lib/fleet-task/point'
import * as Transition from '@/services/rest-api/lib/fleet-task/transition'
import * as DeliveryOrder from '@/services/rest-api/lib/delivery/delivery-order'
import * as Samator from '@/services/rest-api/lib/fleet-task/samator'
import * as Setting from '@/services/rest-api/lib/fleet-task/setting'
import * as Mgm from '@/services/rest-api/lib/fleet-task/mgm'
import * as Contract from '@/services/rest-api/lib/delivery/contract'
import { isEmpty, debounce } from 'lodash'

import { isTmsMarketingUser } from '@/helpers/tms-access'

const cloneFieldConfig = (field) => {
  const clonedField = Object.create(Object.getPrototypeOf(field))
  Object.defineProperties(clonedField, Object.getOwnPropertyDescriptors(field))

  if (field.tableFields && Array.isArray(field.tableFields)) {
    clonedField.tableFields = field.tableFields.map((childField) => {
      const clonedChildField = Object.create(Object.getPrototypeOf(childField))
      Object.defineProperties(clonedChildField, Object.getOwnPropertyDescriptors(childField))

      return clonedChildField
    })
  }

  return clonedField
}

const cloneFieldConfigs = (fields = []) => fields.map((field) => cloneFieldConfig(field))
const vehicleSectionFieldNames = [
  'contract_type',
  'vehicle_category_name',
  'vehicle_type_id',
  'vehicle_id',
  'driver_user_id',
  'driver_user_2_id',
]
const vehicleCategoryDependentFieldNames = [
  'vehicle_type_id',
  'vehicle_id',
  'driver_user_id',
  'driver_user_2_id',
]
const contractTypeDependentFieldNames = [
  'vehicle_category_name',
  'vehicle_type_id',
  'vehicle_id',
  'driver_user_id',
  'driver_user_2_id',
]
const vehicleTypeDependentFieldNames = ['vehicle_id', 'driver_user_id', 'driver_user_2_id']

export default {
  page: {
    title: 'Fleet Task Form',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McWizard,
    McButton,
    Sequence,
    McTransitionStatus,
    OtherOptions,
    McRadioButton,
    Multiselect,
    ModalChangeSeqVendor,
    ModalShare,
    Notification,
    ModalConfirmAutomation,
    McInfoTooltip,
    ModalDeactivatedAutomation,
    DownloadAttachmentModal,
    McBadge,
    TransferDeliveryOrderModal,
    McIconTooltip,
    McAlert,
    TruckScaleTimeline,
  },
  mixins: [MixPanel, Helper],
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'Fleet Task Attach Delivery') {
      this.$store.dispatch('fleetTask/resetAllState')
    }
    next()
  },
  data() {
    return {
      groupBy: 'name',

      formValues: {},
      fleetOrderData: {},

      userGroupField,
      fleetTaskDetailFieldSource: cloneFieldConfigs(temporaryFleetTaskDetailFields),
      fleetTaskDetailFields: cloneFieldConfigs(temporaryFleetTaskDetailFields),
      vendorFleetTaskDetailFields: vendorFleetTaskDetailFields,
      vehicleRelatedFields: [],

      statusTransitions,

      isLoadingInitial: true,

      isLoadingTransition: true,
      showResponse: null,

      formErrors: {},
      codeErrors: {},
      autoCompleteOptions,
      autoCompleteSource: autoCompleteOptions[0],
      isAutoCompleteOn: false,

      initialAutoCompleteSetting: null,

      saveAsDefaultStart: false,
      saveAsDefaultEnd: false,
      disableDefaultStart: false,
      disableDefaultEnd: false,
      sendBy: true,
      isFromVendorRequest: false,
      hasVendor: false,
      fullObjects: {
        user_group_id: null,
        vehicle_id: null,
        driver_user_id: null,
        driver_user_2_id: null,
        vendor_id: null,
        bin_num: null,
        contract_type: null,
        vehicle_type_id: null,
      },

      isDebouncing: false,
      debouncedUpdateFn: null,
      focusedField: null,
      dataFO: {},
      hasErrorNotification: {
        show: false,
        message: null,
      },
      setAutomation: false,
      showToastAutomation: {
        show: false,
        message: null,
        type: 'success',
      },
      isUpdatingVehicleAndDriver: false,
      updateDriver: null,
      updateVehicle: null,
      endIfAllDoDone: false,
      hasDeliveryOrder: false,
      isEstimatedOutsideOperationalTime: false,
      boxType: null,
      isBoxTypeError: false,
      showErrors: {},
      isDisabledBinNum: false,
      attachmentFiles: null,
      buffer: null,
      isEditingVehicleSection: false,
      isSyncingVehicleSectionState: false,
      isResettingVehicleFieldsByContractType: false,
      isResettingVehicleFieldsByCategory: false,
      isResettingVehicleFieldsByType: false,
      isSkippingVehicleSectionAutosave: false,
      vehicleTypeAutosaveGuardValue: undefined,
      isMobileHeader: false,
      hasPageScrolled: false,
      mobileTopbarHeight: 70,
      vehicleSectionSnapshot: {
        formValues: {},
        fullObjects: {},
      },
      vehicleSectionErrorSnapshot: {
        formErrors: {},
        codeErrors: {},
      },
      scaleValue: null,
      sequenceActLoads: [],
      responsePartNum: null,
      responseWarehouseCode: null,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isSamator() {
      const data = this.getFeatureFlagValue('customFeature.samator')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
    isContainLiquid() {
      if (!Array.isArray(this.sequence)) {
        return false
      }
      
      const filtered = this.sequence
        .filter((seq) => seq.type === 'DEFAULT')
        .flatMap((seq) => seq.activities)
      if (filtered.length > 0) {
        const hasLiquid = filtered.some((act) => {
          const productTypeField = act.delivery_order?.user_definition_fields?.find(
            (field) => field?.name === 'Product Type'
          )
          const productTypeValue = (productTypeField?.value || '').toString().trim().toLowerCase()

          return ['bulk', 'tube'].includes(productTypeValue)
        })
        return hasLiquid
      }

      return false
    },
    validateQuantityButtonState() {
      return getValidateQuantityButtonState({
        isSamator: this.isSamator,
        fleetTaskStatus: this.fleetTaskStatus,
        fleetTask: this.showResponse,
        sequence: this.sequence,
        isLoading: this.isLoadingGlobal,
      })
    },
    title() {
      return this.isCreating
        ? this.$t('fleetTask.title.create')
        : `${this.$t('fleetTask.title.detail')} #${
            this.showResponse?.number ?? this.fleetTaskDetail?.number ?? ''
          }`
    },
    mobileHeaderTitle() {
      return this.isCreating ? this.$t('fleetTask.title.create') : this.$t('fleetTask.title.detail')
    },
    mobileHeaderNumber() {
      if (this.isCreating) return ''
      const number = this.showResponse?.number ?? this.fleetTaskDetail?.number ?? ''
      return number ? `#${number}` : ''
    },
    buttonScreenBreakpoint() {
      return 768
    },
    isStickyHeader() {
      return this.isMobileHeader && this.hasPageScrolled
    },
    mobileHeaderCssVars() {
      return {
        '--mc-fleet-task-sticky-top': `${this.mobileTopbarHeight}px`,
      }
    },
    wizardStatusItems() {
      return wizardStatusItems(this)
    },
    wizardStatusItemsMgm() {
      return wizardStatusItemsMgm(this)
    },
    wizardStatusItemsVendor() {
      return wizardStatusItemsVendor(this)
    },
    ...mapState('fleetTask', [
      'selectedDos',
      'sequence',
      'fleetTaskStatus',
      'fleetTaskDetail',
      'isInitial',
      'isLoadingGlobal',
      'isAttachDelivery',
    ]),
    isCreating() {
      return this.showResponse?.status?.name === 'Temporary' ?? false
    },
    // These fields are specifically used to trigger re-fetching sequence when form changes,
    // if these required values are absent, sequence will not be fetched
    missedSequencingRequiredFields() {
      let requiredKeys = ['expected_departure_on', 'start_address_id', 'end_address_id']
      const fields =
        this.fleetTaskStatus == 'Temporary'
          ? temporaryFleetTaskDetailFields
          : draftFleetTaskDetailFields

      const missingKeys = []

      // Loop over required keys, if there's no value in formValues, push the key to missingKeys
      requiredKeys.forEach((key) => {
        if (this.formValues[key] == null) {
          missingKeys.push(key)
        }
      })

      return fields.filter((field) => missingKeys.includes(field.name))
    },
    totalWeight() {
      return formatOrDash(this.showResponse?.current_total_weight, 3)
    },
    totalVolume() {
      return formatOrDash(this.showResponse?.current_total_volume, 3)
    },
    totalWeightCapacity() {
      return formatOrDash(this.showResponse?.vehicle?.box_type_weight_capacity)
    },
    totalVolumeCapacity() {
      return formatOrDash(this.showResponse?.vehicle?.box_type_volume_capacity)
    },
    totalWeightPercentage() {
      return this.showResponse?.load_percentage.weight ?? '-'
    },
    totalVolumePercentage() {
      return this.showResponse?.load_percentage.volume ?? '-'
    },
    totalWeightPercentageStyle() {
      const p = Number(this.totalWeightPercentage)
      if (Number.isNaN(p)) return {}
      if (p > 100) return { color: '#f41d00' }
      if (p < 30) return { color: '#f41d00' }
      if (p <= 60) return { color: '#ff8a00' }
      return { color: '#137c55' }
    },
    totalVolumePercentageStyle() {
      const p = Number(this.totalVolumePercentage)
      if (Number.isNaN(p)) return {}
      if (p > 100) return { color: '#f41d00' }
      if (p < 30) return { color: '#f41d00' }
      if (p <= 60) return { color: '#ff8a00' }
      return { color: '#137c55' }
    },
    hasChanged() {
      if (this.isLoadingGlobal) return false

      let currentFormValues = this.formValues
      let oldFormValues = {}

      if (!this.isCreating) oldFormValues = this.fleetOrderData

      const res = this.filterUnchangedData(currentFormValues, oldFormValues)
      return res[1]
    },
    isNotEditable() {
      if (this.hasVendor && this.fleetTaskStatus === 'Dijadwalkan') {
        return true
      }
      return ['Perjalanan', 'Selesai', 'Dibatalkan', 'Menunggu Vendor', 'Diterima Vendor'].includes(
        this.fleetTaskStatus
      )
    },
    canEditVehicleSection() {
      return (
        this.hasFeatureFleetTaskContractMGM &&
        this.isSendBySelf &&
        this.fleetTaskStatus === 'Dijadwalkan' &&
        !this.isNotEditable
      )
    },
    wizardItems() {
      if (this.fleetTaskStatus === 'Dibatalkan') {
        return this.showResponse?.status.visited_statuses.map((status) => {
          return this.wizardStatusItems[status]
        })
      }

      return [
        this.wizardStatusItems.DRAFT,
        this.wizardStatusItems.SCHEDULED,
        this.wizardStatusItems.STARTED,
        this.wizardStatusItems.ENDED,
      ]
    },
    wizardItemsMgm() {
      const commonStatusesBefore = [
        this.wizardStatusItemsMgm.DRAFT,
        this.wizardStatusItemsMgm.WAITING_FOR_APPROVAL,
      ]

      const commonStatusesAfter = [
        this.wizardStatusItemsMgm.SCHEDULED,
        this.wizardStatusItemsMgm.STARTED,
        this.wizardStatusItemsMgm.ENDED,
      ]

      if (this.fleetTaskStatus === 'Dibatalkan') {
        return this.showResponse?.status?.visited_statuses.map((status) => {
          return this.wizardStatusItemsMgm[status]
        })
      }

      if (
        !this.showResponse?.status?.visited_statuses?.includes('WAITING_FOR_APPROVAL') &&
        !['Temporary', 'Draf', 'Menunggu Persetujuan', 'Disetujui'].includes(this.fleetTaskStatus)
      ) {
        return [
          this.wizardStatusItemsMgm.DRAFT,
          this.wizardStatusItemsMgm.SCHEDULED,
          this.wizardStatusItemsMgm.STARTED,
          this.wizardStatusItemsMgm.ENDED,
        ]
      }

      if (this.fleetTaskStatus === 'Disetujui') {
        return [...commonStatusesBefore, this.wizardStatusItemsMgm.APPROVED, ...commonStatusesAfter]
      }

      return [...commonStatusesBefore, this.wizardStatusItemsMgm.APPROVED, ...commonStatusesAfter]
    },
    wizardItemsVendor() {
      const commonStatusesBefore = [
        this.wizardStatusItemsVendor.DRAFT,
        this.wizardStatusItemsVendor.WAITING_FOR_VENDOR,
      ]

      const commonStatusesAfter = [
        this.wizardStatusItemsVendor.SCHEDULED,
        this.wizardStatusItemsVendor.STARTED,
        this.wizardStatusItemsVendor.ENDED,
      ]

      if (this.fleetTaskStatus === 'Diterima Vendor') {
        return [
          ...commonStatusesBefore,
          this.wizardStatusItemsVendor.ACCEPTED_BY_VENDOR,
          ...commonStatusesAfter,
        ]
      }

      if (this.fleetTaskStatus === 'Ditolak') {
        return [
          ...commonStatusesBefore,
          this.wizardStatusItemsVendor.REJECTED_BY_VENDOR,
          ...commonStatusesAfter,
        ]
      }

      if (this.fleetTaskStatus === 'Dibatalkan') {
        return this.showResponse?.status.visited_statuses.map((status) => {
          return this.wizardStatusItemsVendor[status]
        })
      }

      return [
        ...commonStatusesBefore,
        this.wizardStatusItemsVendor.ACCEPTED_BY_VENDOR,
        ...commonStatusesAfter,
      ]
    },
    hasAccessAutocomplete() {
      return this.hasAccessAll([
        'tms-gps-autocomplete:create',
        'tms-gps-autocomplete:read',
        'tms-gps-autocomplete:update',
        'tms-gps-autocomplete:delete',
      ])
    },
    isSendBySelf() {
      const boolVal = typeof this.sendBy === 'boolean' ? this.sendBy : this.sendBy === 'true'
      return boolVal
    },
    statusTransitionsValue() {
      const { fleetTaskStatus, isFromVendorRequest, hasVendor } = this
      const vehicleAvailable = this.fullObjects?.contract_type?.vehicle_available !== false

      if (fleetTaskStatus === 'Draf') {
        let drafTransitions = statusTransitions.Draf
        if (isFromVendorRequest) {
          drafTransitions = statusTransitions.isFromVendorRequest
        } else if (hasVendor) {
          drafTransitions = statusTransitions.hasVendorDraft
        } else if (!this.hasFeatureFleetTaskContractMGM) {
          drafTransitions = statusTransitions.Draf.filter(
            (item) => item.status.id !== 'WAITING_FOR_APPROVAL'
          )
        }

        if (!vehicleAvailable) {
          drafTransitions = drafTransitions.map((item) => {
            if (['WAITING_FOR_APPROVAL', 'SCHEDULED'].includes(item.status.id)) {
              return {
                ...item,
                disabled: true,
                tooltip: this.$t('fleetTask.formV2.vehicleAndDriver.noAvailabilityTooltip'),
              }
            }
            return item
          })
        }

        return { [fleetTaskStatus]: drafTransitions }
      }

      if (hasVendor) {
        if (fleetTaskStatus === 'Ditolak') {
          return { [fleetTaskStatus]: statusTransitions.Ditolak }
        }
        if (fleetTaskStatus !== 'Draf') {
          return { [fleetTaskStatus]: statusTransitions.hasVendor }
        }
      }

      if (fleetTaskStatus === 'Dijadwalkan') {
        let scheduledTransitions = statusTransitions.Dijadwalkan
        if (isFromVendorRequest) {
          scheduledTransitions = scheduledTransitions.filter(
            (transition) => transition.status.id !== 'CANCELED'
          )
        }

        if (!vehicleAvailable) {
          scheduledTransitions = scheduledTransitions.map((item) => {
            if (item.status.id === 'STARTED') {
              return {
                ...item,
                disabled: true,
                tooltip: this.$t('fleetTask.formV2.vehicleAndDriver.noAvailabilityTooltip'),
              }
            }
            return item
          })
        }

        return { [fleetTaskStatus]: scheduledTransitions }
      }

      return statusTransitions
    },
    showLastUpdated() {
      const lastUpdated = this.showResponse?.updated_on ?? this.showResponse?.created_on

      if (!lastUpdated) return null

      const formattedDate = this.formatIndexDate(lastUpdated)
      const isToday = formattedDate.date === moment().format('DD MMM YYYY')

      return isToday ? formattedDate.time : `${formattedDate.date}, ${formattedDate.time}`
    },
    originCity() {
      const pickupPoint = this.sequence.find(
        (point) =>
          point.type === 'DEFAULT' &&
          point.activities?.some((activity) => activity.type === 'PICKUP')
      )
      const deliveryOrderId = pickupPoint?.activities?.find(
        (activity) => activity.type === 'PICKUP'
      )?.delivery_order?.id
      const selectedDo = this.selectedDos.find((item) => item.delivery_order_id === deliveryOrderId)

      return selectedDo?.origin_address?.city ?? '-'
    },
    destinationCity() {
      const dropPoints = this.sequence.filter(
        (point) =>
          point.type === 'DEFAULT' && point.activities?.some((activity) => activity.type === 'DROP')
      )

      const lastDropPoint = dropPoints[dropPoints.length - 1]
      const deliveryOrderId = lastDropPoint?.activities?.find(
        (activity) => activity.type === 'DROP'
      )?.delivery_order?.id
      const selectedDo = this.selectedDos.find((item) => item.delivery_order_id === deliveryOrderId)

      return selectedDo?.destination_address?.city ?? '-'
    },
    vendorField() {
      return this.vendorFleetTaskDetailFields.map((field) => {
        if (field.headerInfoComponent) {
          const newItems = field.headerInfoProps.items.map((item) => {
            const newItem = { ...item }

            switch (item.key) {
              case 'origin':
                newItem.value = this.originCity || '-'
                break
              case 'destination':
                newItem.value = this.destinationCity || '-'
                break
              case 'total_weight':
                newItem.value = `${this.totalWeight} Kg` || '-'
                break
              case 'total_volume':
                newItem.value = `${this.totalVolume} Cbm` || '-'
                break
            }

            return newItem
          })

          return {
            ...field,
            headerInfoProps: {
              ...field.headerInfoProps,
              items: newItems,
            },
          }
        }

        return field
      })
    },
    showAddDeliveryButton() {
      const originalCondition =
        !this.isNotEditable && !(this.hasVendor === false && this.isFromVendorRequest === true)

      const interventionCondition =
        this.fleetTaskStatus === 'Perjalanan' &&
        this.hasVendor === false &&
        this.isFromVendorRequest === false

      return originalCondition || interventionCondition
    },
    isEnabledAutomation() {
      return (
        !this.showResponse?.is_auto_started && this.showResponse?.auto_start_type !== 'GEOFENCE'
      )
    },
    isOverlap() {
      return this.showResponse?.is_overlap
    },
    buttonFleetTaskAutomation() {
      let value = {
        variant: 'activated',
        text: this.$t('fleetTask.action.travelAutomationEnabled'),
        icon: 'icon-fill_flash',
        disabled:
          this.isUserGroupNull ||
          this.isTmsMarketing ||
          this.isLoadingGlobal ||
          !this.isSendBySelf ||
          this.isOverlap ||
          (this.hasFeatureFleetTaskContractMGM &&
            ['Draf', 'Menunggu Persetujuan'].includes(this.fleetTaskStatus)),
        displayTooltip: true,
        forceShowTooltip: true,
        tooltipText: this.isOverlap
          ? this.$t('fleetTask.formV2.automation.enableTooltipOverlap')
          : this.isSendBySelf
          ? this.$t('fleetTask.formV2.automation.enableTooltipSelf')
          : this.$t('fleetTask.formV2.automation.enableTooltipVendor'),
      }

      if (!this.isEnabledAutomation) {
        value = {
          variant: 'deactivated',
          text: this.$t('fleetTask.action.travelAutomationDisabled'),
          icon: 'icon-fill_slash',
          disabled: this.isUserGroupNull || this.isLoadingGlobal,
          displayTooltip: true,
          forceShowTooltip: true,
          tooltipText: this.$t('fleetTask.formV2.automation.disableTooltip'),
        }
      }

      return value
    },
    sameOriginAndFirstDoPickup() {
      return this.showResponse?.is_start_and_first_point_same ?? null
    },
    showBannerAutomation() {
      if (['Perjalanan'].includes(this.fleetTaskStatus) && !this.isOverlap) return false
      if (
        ![
          'Dijadwalkan',
          'Perjalanan',
          'Menunggu Vendor',
          'Ditolak Vendor',
          'Diterima Vendor',
        ].includes(this.fleetTaskStatus) ||
        this.hasVendor
      )
        return false

      const totalError = Object.keys(this.showErrors || {}).length

      const base = {
        variant: 'success',
        icon: 'icon-fill_flash',
        message: this.$t('fleetTask.formV2.automation.baseMessage'),
      }

      const failMessages = {
        driver_user_id: {
          variant: 'failed',
          icon: 'icon-fill_close',
          message: this.$t('fleetTask.formV2.automation.driverConflictMessage'),
        },
        vehicle_id: {
          variant: 'failed',
          icon: 'icon-fill_close',
          message: this.$t('fleetTask.formV2.automation.vehicleConflictMessage'),
        },
        vehicle_driver_conflict: {
          variant: 'failed',
          icon: 'icon-fill_close',
          message: this.$t('fleetTask.formV2.automation.vehicleDriverConflictMessage'),
        },
      }

      const overlapFailAutoStart = {
        variant: 'failed',
        icon: 'icon-fill_close',
        message: this.$t('fleetTask.formV2.automation.overlapFailAutoStart'),
      }

      const overlapFail = {
        variant: 'warning',
        icon: 'icon-fill_alert-triangle',
        message: this.$t('fleetTask.formV2.automation.overlapFail'),
      }

      const overlapFailStarted = {
        variant: 'warning',
        icon: 'icon-fill_alert-triangle',
        message: this.$t('fleetTask.formV2.automation.overlapFailStarted'),
      }

      const geoSamePickup = {
        variant: 'success',
        icon: 'icon-fill_flash',
        message: this.$t('fleetTask.formV2.automation.geoSamePickup'),
      }

      const differentBoxType = {
        variant: 'failed',
        icon: 'icon-fill_close',
        message: this.$t('fleetTask.formV2.automation.differentBoxType'),
      }

      const fieldErrorKeys = Object.keys(this.showErrors || {})
      const multiErrors = fieldErrorKeys.map((fieldKey) => {
        const errorCode = this.showErrors[fieldKey][0]
        return {
          key: fieldKey,
          message: apiErrorMappings[fieldKey]?.[errorCode] ?? null,
          action: actionErrorMappings[fieldKey]?.[errorCode] ?? null,
        }
      })

      const multipleErrors = {
        variant: 'failed',
        icon: 'icon-fill_close',
        message: this.$t('fleetTask.formV2.automation.multipleErrors', { count: totalError }),
        isMulti: true,
        multiErrors: multiErrors,
      }

      if (failMessages[this.autoStartFailed?.key]) return failMessages[this.autoStartFailed.key]
      if (
        this.showResponse?.is_overlap &&
        this.fleetTaskStatus === 'Dijadwalkan' &&
        this.showResponse?.auto_start_type === 'GEOFENCE'
      )
        return overlapFailAutoStart
      if (
        this.showResponse?.is_overlap &&
        this.fleetTaskStatus === 'Dijadwalkan' &&
        this.showResponse?.auto_start_type !== 'GEOFENCE'
      )
        return overlapFail

      if (!this.showResponse?.auto_start_type) return false

      if (this.showResponse?.is_overlap && this.fleetTaskStatus === 'Perjalanan')
        return overlapFailStarted
      if (!this.showResponse?.is_overlap && this.sameOriginAndFirstDoPickup) return geoSamePickup

      if (this.isBoxTypeError && totalError === 1) return differentBoxType

      if (totalError > 1) return multipleErrors

      return base
    },
    showAlertTransition() {
      if (this.fleetTaskStatus !== 'Dijadwalkan') return null
      const base = {
        show: false,
        variant: 'warning',
        html: '',
      }
      if (
        this.showResponse?.is_overlap &&
        this.fleetTaskStatus === 'Dijadwalkan' &&
        this.showResponse?.auto_start_type === 'GEOFENCE'
      ) {
        base.show = true
        base.variant = 'warning'
        base.html = this.$t('fleetTask.formV2.automation.alertDisabled')
      }

      return base
    },
    hasTmsUserGroupAccess() {
      return this.hasAccessAll(['tms-user-group:read']) && this.isSuperUser()
    },
    responseUserGroup() {
      return this.showResponse?.user_group?.name ?? '-'
    },
    isUserGroupNull() {
      // Disable all inputs when user_group from response is null
      return (
        this.hasTmsUserGroupAccess &&
        this.showResponse &&
        !this.showResponse.user_group &&
        ['Draf', 'Dijadwalkan', 'Ditolak', 'Perjalanan'].includes(this.fleetTaskStatus)
      )
    },
    transferableOrderIds() {
      const transferableOrderIds = []

      this.selectedDos.forEach((delivery) => {
        if (this.canTransferDeliveryOrder(delivery)) {
          transferableOrderIds.push(delivery.id)
        }
      })

      return transferableOrderIds
    },
    showBulkTransferButton() {
      return (
        !this.hasVendor &&
        !this.isFromVendorRequest &&
        ['Dijadwalkan', 'Perjalanan'].includes(this.fleetTaskStatus) &&
        this.transferableOrderIds.length > 0
      )
    },
    hasBinNumberSamator() {
      const data = this.getFeatureFlagValue('customFeature.samator')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
    hasFeatureFleetTaskContractMGM() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data?.features?.fleet_task_contract
      )
    },
    hasFeatureBufferTimeMGM() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data?.features?.buffer_time
      )
    },
    hasDefaultEndFOByDOMGM() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data?.features?.default_end_fo_by_do
      )
    },
    truckLoadType() {
      const isFullTruckLoad = this.showResponse?.truck_load_type === 'FTL'

      return isFullTruckLoad
        ? {
            label: this.$t('fleetTask.formV2.truckLoadType.ftlLabel'),
            tooltip: this.$t('fleetTask.formV2.truckLoadType.ftlTooltip'),
          }
        : {
            label: this.$t('fleetTask.formV2.truckLoadType.ltlLabel'),
            tooltip: this.$t('fleetTask.formV2.truckLoadType.ltlTooltip'),
          }
    },
    isLessTruckLoad() {
      return this.showResponse?.truck_load_type === 'LTL'
    },
    isFullTruckLoadImmediate() {
      return (
        this.showResponse?.truck_load_type === 'FTL' &&
        this.showResponse?.customer?.mgm_immediate_type === 'Immediate'
      )
    },
    isTmsMarketing() {
      return isTmsMarketingUser()
    },
  },
  watch: {
    '$route.params.id': function () {
      this.showDetailData()
    },
    isContainLiquid() {
      this.updateFleetTaskDetailFields()
    },
    'formValues.contract_type': function (newVal, oldVal) {
      const selectedContractType =
        newVal && typeof newVal === 'object' ? newVal.id || null : newVal || null
      setFleetTaskCustomerContractType(selectedContractType)

      if (this.isLoadingInitial || this.isAttachDelivery || this.isLoadingGlobal) return
      if (
        this.isSyncingVehicleSectionState ||
        this.isResettingVehicleFieldsByContractType ||
        this.isResettingVehicleFieldsByCategory ||
        this.isResettingVehicleFieldsByType
      )
        return
      if (isEqual(newVal, oldVal)) return

      if (selectedContractType) {
        this.fetchContractTypeDetails(selectedContractType)
      }
      this.resetVehicleFieldsByContractType()
    },
    'formValues.vehicle_category_name': function (newVal, oldVal) {
      const selectedCategory =
        newVal && typeof newVal === 'object' ? newVal.category || null : newVal || null
      setFleetTaskSelectedVehicleCategory(selectedCategory)

      if (this.isLoadingInitial || this.isAttachDelivery || this.isLoadingGlobal) return
      if (
        this.isSyncingVehicleSectionState ||
        this.isResettingVehicleFieldsByContractType ||
        this.isResettingVehicleFieldsByCategory ||
        this.isResettingVehicleFieldsByType
      )
        return
      if (isEqual(newVal, oldVal)) return

      this.resetVehicleFieldsByCategory()
    },
    'formValues.vehicle_type_id': function (newVal, oldVal) {
      const selectedVehicleTypeId =
        newVal && typeof newVal === 'object' ? newVal.vehicleTypeId || null : newVal || null
      setFleetTaskSelectedVehicleTypeId(selectedVehicleTypeId)

      if (this.isLoadingInitial || this.isAttachDelivery || this.isLoadingGlobal) return
      if (
        this.isSyncingVehicleSectionState ||
        this.isResettingVehicleFieldsByContractType ||
        this.isResettingVehicleFieldsByCategory ||
        this.isResettingVehicleFieldsByType
      )
        return
      if (isEqual(newVal, oldVal)) return

      this.vehicleTypeAutosaveGuardValue = newVal ?? null
      this.resetVehicleFieldsByType()
    },
    'formValues.vehicle_id': function (newVal, oldVal) {
      const selectedVehicleId =
        newVal && typeof newVal === 'object' ? newVal.id || null : newVal || null
      setFleetTaskSelectedVehicleId(selectedVehicleId)
      this.markVehicleSectionAutosaveSkip(newVal, oldVal)
    },
    'formValues.driver_user_id': function (newVal, oldVal) {
      const selectedDriverUserId =
        newVal && typeof newVal === 'object' ? newVal.id || null : newVal || null
      setFleetTaskSelectedDriverUserId(selectedDriverUserId)
      this.markVehicleSectionAutosaveSkip(newVal, oldVal)
    },
    'formValues.driver_user_2_id': function (newVal, oldVal) {
      const selectedDriverUser2Id =
        newVal && typeof newVal === 'object' ? newVal.id || null : newVal || null
      setFleetTaskSelectedDriverUser2Id(selectedDriverUser2Id)
      this.markVehicleSectionAutosaveSkip(newVal, oldVal)
    },
    formValues: {
      deep: true,
      handler(newVal, oldVal) {
        const isSkipEnabled = this.isVehicleSectionWatcherSkipEnabled()
        if (this.isDebouncing || this.isLoadingInitial || this.isAttachDelivery) return
        if (isSkipEnabled && this.isSkippingVehicleSectionAutosave) {
          this.isSkippingVehicleSectionAutosave = false
          return
        }
        if (
          isSkipEnabled &&
          (this.isResettingVehicleFieldsByContractType ||
            this.isResettingVehicleFieldsByCategory ||
            this.isResettingVehicleFieldsByType)
        )
          return
        if (isSkipEnabled && this.shouldSkipVehicleSectionAutosave(newVal, oldVal)) return

        const currentVehicleTypeId = this.formValues?.vehicle_type_id ?? null
        if (isSkipEnabled && this.vehicleTypeAutosaveGuardValue === undefined) {
          this.vehicleTypeAutosaveGuardValue = currentVehicleTypeId
        } else if (
          isSkipEnabled &&
          !isEqual(currentVehicleTypeId, this.vehicleTypeAutosaveGuardValue)
        ) {
          this.vehicleTypeAutosaveGuardValue = currentVehicleTypeId
          return
        }

        if (JSON.stringify(newVal) === JSON.stringify(oldVal)) {
          this.updateDetailsAndGetSequence()
          return
        }
        if (
          newVal.vendor_id &&
          this.hasVendor &&
          this.fleetTaskStatus !== 'Draf' &&
          this.fleetTaskStatus !== 'Ditolak'
        ) {
          Object.entries(this.parseData(this.showResponse)).forEach(([key, value]) => {
            this.$set(this.formValues, key, value)
            this.handleFullObject({ key, value })
          })
          this.updateDetailsAndGetSequence()
          return
        }
        this.updateDetailsAndGetSequence()
      },
    },
    showResponse(newData) {
      if (!newData) return
      this.$store.dispatch('fleetTask/setFleetTaskDetail', newData)
    },
    autoCompleteSource(newData) {
      if (this.initialAutoCompleteSetting.autocomplete_source === newData.value) return
      this.updateAutocompleteSetting({ autocomplete_source: newData.value })
    },
    isAutoCompleteOn(newData) {
      const boolVal = typeof newData === 'boolean' ? newData : newData === 'true'

      if (this.initialAutoCompleteSetting.is_autocomplete_on === boolVal) return

      this.updateAutocompleteSetting({ is_autocomplete_on: boolVal })
    },
    'formValues.start_address_id': function (newValue) {
      this.$set(this.formValues, 'end_address_id', newValue)
    },
    saveAsDefaultStart: function (newValue) {
      if (!this.initiatingSaveAsDefaultToggle) {
        this.updateDefaultAddressSettings({ start_address_toggle: newValue })
      }
    },
    saveAsDefaultEnd: function (newValue) {
      if (!this.initiatingSaveAsDefaultToggle) {
        this.updateDefaultAddressSettings({ end_address_toggle: newValue })
      }
    },
    isSendBySelf(newValue) {
      if (newValue === true) {
        this.isAutoCompleteOn = false
        this.hasVendor = false
      }
    },
    '$i18n.locale': function () {
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.hasValidationErrors()
        }
      })

      for (let [key, errorCode] of Object.entries(this.codeErrors)) {
        this.$set(this.formErrors, key, this.$t(errorCode))
      }
    },
    endIfAllDoDone(newValue) {
      const auto_end_type = newValue ? 'ACTIVITIES_COMPLETION' : null
      this.$set(this.formValues, 'auto_end_type', auto_end_type)
      this.updateDetailsAndGetSequence()
    },
  },
  created() {
    if (!this.isAttachDelivery) {
      this.$store.dispatch('fleetTask/setIsInitial', true)
    }
    EventBus.$on('refetch-sequence', this.showDetailData)
    EventBus.$on('update-has-vendor', () => {
      this.sendBy = 'false'
      this.hasVendor = false
    })

    this.debouncedUpdateFn = debounce(this.applyTextUpdates, 500)
  },
  async mounted() {
    this.checkHeaderViewport()
    this.handleWindowScroll()
    this.updateMobileLayoutMetrics()
    window.addEventListener('resize', this.checkHeaderViewport)
    window.addEventListener('scroll', this.handleWindowScroll, { passive: true })
    window.addEventListener('resize', this.updateMobileLayoutMetrics)

    if (this.$route.params.id) {
      await this.showDetailData()
    }

    if (this.isAttachDelivery) {
      await this.$store.dispatch('fleetTask/setIsLoadingGlobalDelay', false)
      this.$store.dispatch('fleetTask/setIsAttachDelivery', false)
    }

    if (this.$route.query.isRecreateFO) {
      this.updateDetailFO()
    }

    if (this.$route.query.isCreateFoFromBulkDo) {
      const transition = {
        status: {
          id: 'Draf',
          name: 'Draf',
          label: 'fleetTask.status.draft',
        },
      }
      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              tag: 'strong',
              text: `${this.$route.query.totalDeliveries}`,
            },
            { text: this.$t('fleetTask.formV2.alerts.deliverySavedAndScheduled') },
          ],
        },
      })
      this.showSaveSuccessToast(transition)
    }

    this.isLoadingInitial = false
    this.vehicleTypeAutosaveGuardValue = this.formValues?.vehicle_type_id ?? null
    this.$nextTick(() => {
      this.updateMobileLayoutMetrics()
    })
  },
  beforeDestroy() {
    EventBus.$off('refetch-sequence', this.showDetailData)
    window.removeEventListener('resize', this.checkHeaderViewport)
    window.removeEventListener('scroll', this.handleWindowScroll)
    window.removeEventListener('resize', this.updateMobileLayoutMetrics)
    setFleetTaskSelectedVehicleCategory(undefined)
    setFleetTaskSelectedDriverUserId(undefined)
    setFleetTaskSelectedDriverUser2Id(undefined)
    setFleetTaskSelectedVehicleId(undefined)
    setFleetTaskSelectedVehicleTypeId(undefined)
    setFleetTaskCustomerContractType(undefined)
  },
  methods: {
    formatDateWithTz,
    formatIndexDate,
    isEmpty,
    handleWindowScroll() {
      this.hasPageScrolled = window.scrollY > 8
      this.updateMobileLayoutMetrics()
    },
    checkHeaderViewport() {
      this.isMobileHeader = window.innerWidth < this.buttonScreenBreakpoint
      this.updateMobileLayoutMetrics()
    },
    updateMobileLayoutMetrics() {
      if (!this.isMobileHeader) return

      this.$nextTick(() => {
        const topbar = document.getElementById('page-topbar')
        this.mobileTopbarHeight = Math.ceil(topbar?.getBoundingClientRect().height || 70)
      })
    },
    getFleettaskStatusLabel(status) {
      if (!status) return '-'
      if (status.label) return this.$t(status.label)

      const statusId = status.id || statusNameToIdMap[status.name]
      const statusLabel = statusIdToLabelMap[statusId]

      return statusLabel ? this.$t(statusLabel) : status.name
    },
    isVehicleSectionWatcherSkipEnabled() {
      return this.fleetTaskStatus === 'Dijadwalkan'
    },
    markVehicleSectionAutosaveSkip(newVal, oldVal) {
      if (!this.isVehicleSectionWatcherSkipEnabled()) return
      if (!this.isEditingVehicleSection) return
      if (this.isLoadingInitial || this.isAttachDelivery || this.isLoadingGlobal) return
      if (
        this.isSyncingVehicleSectionState ||
        this.isResettingVehicleFieldsByContractType ||
        this.isResettingVehicleFieldsByCategory ||
        this.isResettingVehicleFieldsByType
      )
        return
      if (isEqual(newVal, oldVal)) return

      this.isSkippingVehicleSectionAutosave = true
    },
    async resetFormErrors() {
      this.formErrors = {}
    },
    isVehicleSectionField(name) {
      return vehicleSectionFieldNames.includes(name)
    },
    isVehicleSectionErrorKey(key) {
      if (!key) return false
      if (this.isVehicleSectionField(key)) return true

      const normalizedKey = key.replace(/^fields\./, '')
      if (this.isVehicleSectionField(normalizedKey)) return true

      const lastKeySegment = normalizedKey.split('.').pop()
      return this.isVehicleSectionField(lastKeySegment)
    },
    shouldSkipVehicleSectionAutosave(newVal, oldVal) {
      if (!this.isVehicleSectionWatcherSkipEnabled()) return false
      if (!this.isEditingVehicleSection) return false
      if (this.isSyncingVehicleSectionState) return true

      const changedKeys = Object.keys({ ...oldVal, ...newVal }).filter(
        (key) => !isEqual(newVal?.[key], oldVal?.[key])
      )

      // Vue deep watcher can pass identical refs for old/new values, resulting in empty changedKeys.
      // During vehicle-section edit mode, treat it as skippable to avoid unintended autosave to BE.
      if (changedKeys.length === 0) return true

      return changedKeys.every((key) => this.isVehicleSectionField(key))
    },
    isVehicleSectionFieldDisabled(name) {
      return (
        this.canEditVehicleSection &&
        this.isVehicleSectionField(name) &&
        !this.isEditingVehicleSection
      )
    },
    clearVehicleSectionErrors() {
      Object.keys(this.formErrors || {}).forEach((key) => {
        if (this.isVehicleSectionErrorKey(key)) {
          this.$delete(this.formErrors, key)
        }
      })

      Object.keys(this.codeErrors || {}).forEach((key) => {
        if (this.isVehicleSectionErrorKey(key)) {
          this.$delete(this.codeErrors, key)
        }
      })

      Object.keys(this.showErrors || {}).forEach((key) => {
        if (this.isVehicleSectionErrorKey(key)) {
          this.$delete(this.showErrors, key)
        }
      })
    },
    getVehicleSectionErrorsSnapshot() {
      const formErrors = {}
      const codeErrors = {}
      const showErrors = {}

      Object.keys(this.formErrors || {}).forEach((key) => {
        if (this.isVehicleSectionErrorKey(key)) {
          formErrors[key] = this.formErrors[key]
        }
      })

      Object.keys(this.codeErrors || {}).forEach((key) => {
        if (this.isVehicleSectionErrorKey(key)) {
          codeErrors[key] = this.codeErrors[key]
        }
      })

      Object.keys(this.showErrors || {}).forEach((key) => {
        if (this.isVehicleSectionErrorKey(key)) {
          showErrors[key] = this.showErrors[key]
        }
      })

      return { formErrors, codeErrors, showErrors }
    },
    restoreVehicleSectionErrors() {
      this.clearVehicleSectionErrors()

      Object.entries(this.vehicleSectionErrorSnapshot?.formErrors || {}).forEach(([key, value]) => {
        this.$set(this.formErrors, key, value)
      })

      Object.entries(this.vehicleSectionErrorSnapshot?.codeErrors || {}).forEach(([key, value]) => {
        this.$set(this.codeErrors, key, value)
      })

      Object.entries(this.vehicleSectionErrorSnapshot?.showErrors || {}).forEach(([key, value]) => {
        this.$set(this.showErrors, key, value)
      })
    },
    clearContractTypeDependentErrors() {
      Object.keys(this.formErrors || {}).forEach((key) => {
        const normalizedKey = key.replace(/^fields\./, '')
        const lastKeySegment = normalizedKey.split('.').pop()

        if (contractTypeDependentFieldNames.includes(lastKeySegment)) {
          this.$delete(this.formErrors, key)
        }
      })

      Object.keys(this.codeErrors || {}).forEach((key) => {
        const normalizedKey = key.replace(/^fields\./, '')
        const lastKeySegment = normalizedKey.split('.').pop()

        if (contractTypeDependentFieldNames.includes(lastKeySegment)) {
          this.$delete(this.codeErrors, key)
        }
      })
    },
    clearVehicleCategoryDependentErrors() {
      Object.keys(this.formErrors || {}).forEach((key) => {
        const normalizedKey = key.replace(/^fields\./, '')
        const lastKeySegment = normalizedKey.split('.').pop()

        if (vehicleCategoryDependentFieldNames.includes(lastKeySegment)) {
          this.$delete(this.formErrors, key)
        }
      })

      Object.keys(this.codeErrors || {}).forEach((key) => {
        const normalizedKey = key.replace(/^fields\./, '')
        const lastKeySegment = normalizedKey.split('.').pop()

        if (vehicleCategoryDependentFieldNames.includes(lastKeySegment)) {
          this.$delete(this.codeErrors, key)
        }
      })
    },
    clearVehicleTypeDependentErrors() {
      Object.keys(this.formErrors || {}).forEach((key) => {
        const normalizedKey = key.replace(/^fields\./, '')
        const lastKeySegment = normalizedKey.split('.').pop()

        if (vehicleTypeDependentFieldNames.includes(lastKeySegment)) {
          this.$delete(this.formErrors, key)
        }
      })

      Object.keys(this.codeErrors || {}).forEach((key) => {
        const normalizedKey = key.replace(/^fields\./, '')
        const lastKeySegment = normalizedKey.split('.').pop()

        if (vehicleTypeDependentFieldNames.includes(lastKeySegment)) {
          this.$delete(this.codeErrors, key)
        }
      })
    },
    resetVehicleFieldsByContractType() {
      const isSkipEnabled = this.isVehicleSectionWatcherSkipEnabled()
      if (isSkipEnabled && this.isResettingVehicleFieldsByContractType) return
      if (isSkipEnabled) {
        this.isResettingVehicleFieldsByContractType = true
      }

      contractTypeDependentFieldNames.forEach((fieldName) => {
        this.$set(this.formValues, fieldName, null)
        this.$set(this.fullObjects, fieldName, null)
      })

      this.vehicleTypeAutosaveGuardValue = this.formValues?.vehicle_type_id ?? null
      this.updateDriver = null
      this.isUpdatingVehicleAndDriver = false
      this.clearContractTypeDependentErrors()

      if (isSkipEnabled) {
        this.$nextTick(() => {
          this.isResettingVehicleFieldsByContractType = false
        })
      }
    },
    resetVehicleFieldsByCategory() {
      const isSkipEnabled = this.isVehicleSectionWatcherSkipEnabled()
      if (isSkipEnabled && this.isResettingVehicleFieldsByCategory) return
      if (isSkipEnabled) {
        this.isResettingVehicleFieldsByCategory = true
      }

      vehicleCategoryDependentFieldNames.forEach((fieldName) => {
        this.$set(this.formValues, fieldName, null)
        this.$set(this.fullObjects, fieldName, null)
      })

      this.vehicleTypeAutosaveGuardValue = this.formValues?.vehicle_type_id ?? null
      this.updateDriver = null
      this.isUpdatingVehicleAndDriver = false
      this.clearVehicleCategoryDependentErrors()

      if (isSkipEnabled) {
        this.$nextTick(() => {
          this.isResettingVehicleFieldsByCategory = false
        })
      }
    },
    resetVehicleFieldsByType() {
      const isSkipEnabled = this.isVehicleSectionWatcherSkipEnabled()
      if (isSkipEnabled && this.isResettingVehicleFieldsByType) return
      if (isSkipEnabled) {
        this.isResettingVehicleFieldsByType = true
      }

      vehicleTypeDependentFieldNames.forEach((fieldName) => {
        this.$set(this.formValues, fieldName, null)
        this.$set(this.fullObjects, fieldName, null)
      })

      this.vehicleTypeAutosaveGuardValue = this.formValues?.vehicle_type_id ?? null
      this.updateDriver = null
      this.isUpdatingVehicleAndDriver = false
      this.clearVehicleTypeDependentErrors()

      if (isSkipEnabled) {
        this.$nextTick(() => {
          this.isResettingVehicleFieldsByType = false
        })
      }
    },
    getVehicleSectionServerSnapshot() {
      const parsedData = this.parseData(this.showResponse || {})
      const customField = this.showResponse?.custom_field || {}

      const contractType = customField?.contract_type || null

      const contractTypeName =
        customField?.contract_type
          ?.toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase()) || null

      const vehicleCategoryName =
        customField?.vehicle_category_name ??
        this.showResponse?.vehicle_category_name?.category ??
        this.showResponse?.vehicle_category_name ??
        null

      const vehicleTypeId =
        customField?.vehicle_type_id ??
        this.showResponse?.vehicle_type_id?.vehicleTypeId ??
        this.showResponse?.vehicle_type_id ??
        null

      const vehicleTypeName =
        customField?.vehicle_type_name ??
        this.showResponse?.vehicle_type_id?.type ??
        this.showResponse?.vehicle_type_id?.name ??
        null

      return {
        formValues: {
          ...pick(parsedData, vehicleSectionFieldNames),
          contract_type: contractType,
          vehicle_category_name: vehicleCategoryName,
          vehicle_type_id: vehicleTypeId,
        },
        fullObjects: {
          contract_type: contractType
            ? {
                id: contractType,
                name: contractTypeName,
              }
            : null,
          vehicle_category_name: vehicleCategoryName
            ? {
                category: vehicleCategoryName,
                name: vehicleCategoryName,
              }
            : null,
          vehicle_type_id: vehicleTypeId
            ? {
                id: vehicleTypeId,
                type: vehicleTypeName,
                name: vehicleTypeName,
              }
            : null,
          vehicle_id: this.showResponse?.vehicle ?? null,
          driver_user_id: this.showResponse?.driver_user ?? null,
          driver_user_2_id: this.showResponse?.driver_user_2 ?? null,
        },
      }
    },
    setVehicleSectionEditing(value) {
      this.isEditingVehicleSection = value
      if (!value) {
        this.vehicleSectionSnapshot = {
          formValues: {},
          fullObjects: {},
        }
        this.vehicleSectionErrorSnapshot = {
          formErrors: {},
          codeErrors: {},
        }
      }
    },
    handleVehicleSectionEdit() {
      const serverSnapshot = this.getVehicleSectionServerSnapshot()
      const currentFormValues = pick(this.formValues, vehicleSectionFieldNames)
      const currentFullObjects = pick(this.fullObjects, vehicleSectionFieldNames)

      vehicleSectionFieldNames.forEach((fieldName) => {
        const currentFormValue = currentFormValues?.[fieldName]
        const currentFullObject = currentFullObjects?.[fieldName]
        const hasCurrentFormValue =
          currentFormValue !== undefined && currentFormValue !== null && currentFormValue !== ''
        const hasCurrentFullObject = currentFullObject !== undefined && currentFullObject !== null

        if (currentFormValue !== undefined) {
          serverSnapshot.formValues[fieldName] = currentFormValue
        } else {
          serverSnapshot.formValues[fieldName] = serverSnapshot.formValues?.[fieldName] ?? null
        }

        if (hasCurrentFullObject) {
          serverSnapshot.fullObjects[fieldName] = currentFullObject
        } else if (!hasCurrentFormValue && currentFullObject === null) {
          serverSnapshot.fullObjects[fieldName] = null
        } else {
          serverSnapshot.fullObjects[fieldName] = serverSnapshot.fullObjects?.[fieldName] ?? null
        }
      })

      this.vehicleSectionSnapshot = serverSnapshot
      this.vehicleSectionErrorSnapshot = this.getVehicleSectionErrorsSnapshot()
      this.clearVehicleSectionErrors()
      this.setVehicleSectionEditing(true)
    },
    async handleVehicleSectionCancel() {
      const serverSnapshot = this.getVehicleSectionServerSnapshot()
      this.isSyncingVehicleSectionState = true

      vehicleSectionFieldNames.forEach((fieldName) => {
        const snapshotFormValue = this.vehicleSectionSnapshot.formValues?.[fieldName]
        const fallbackFormValue = serverSnapshot.formValues?.[fieldName]
        const restoredFormValue =
          snapshotFormValue !== undefined ? snapshotFormValue : fallbackFormValue

        const snapshotFullObject = this.vehicleSectionSnapshot.fullObjects?.[fieldName]
        const fallbackFullObject = serverSnapshot.fullObjects?.[fieldName]
        const hasRestoredFormValue =
          restoredFormValue !== undefined && restoredFormValue !== null && restoredFormValue !== ''
        const hasSnapshotFullObject =
          snapshotFullObject !== undefined && snapshotFullObject !== null
        let restoredFullObject = fallbackFullObject

        if (hasSnapshotFullObject) {
          restoredFullObject = snapshotFullObject
        } else if (snapshotFullObject === null && !hasRestoredFormValue) {
          restoredFullObject = null
        }

        this.$set(this.formValues, fieldName, restoredFormValue ?? null)
        this.$set(this.fullObjects, fieldName, restoredFullObject ?? null)
      })

      this.vehicleTypeAutosaveGuardValue = this.formValues?.vehicle_type_id ?? null
      this.updateDriver = null
      this.isUpdatingVehicleAndDriver = false
      this.restoreVehicleSectionErrors()

      await this.$nextTick()
      this.isSyncingVehicleSectionState = false
      this.setVehicleSectionEditing(false)
    },
    async handleVehicleSectionSave() {
      const isSuccess = await this.updateDetailsAndGetSequence()
      if (isSuccess) {
        this.setVehicleSectionEditing(false)
      }
    },
    async updateDetailsAndGetSequence() {
      if (this.isLoadingGlobal) return false
      await this.$store.dispatch('fleetTask/setIsLoadingGlobalDelay', true)
      this.$store.dispatch('fleetTask/setIsInitial', false)

      // Remove hash if exist to avoid scroll on update details field
      if (this.$route.hash) {
        await this.$router.replace({ hash: '' })
      }

      await this.resetFormErrors()
      const isSuccess = await this.updateDetails(this.formValues)
      if (!isSuccess) {
        await this.$store.dispatch('fleetTask/setIsLoadingGlobalDelay', false)
        return false
      }
      await this.getSequence()
      await this.$store.dispatch('fleetTask/setIsLoadingGlobalDelay', false)
      return true
    },
    async getSequence() {
      const res = await FleetTaskPoint.get(this.$route.params.id)

      if (res.errors) return

      const seq = res.data

      const hasStartPoint = seq.find((item) => item.type === 'START')
      const hasEndPoint = seq.find((item) => item.type === 'END')
      if (!hasStartPoint) seq.unshift({ type: 'START' })
      if (!hasEndPoint) seq.push({ type: 'END' })

      await this.$store.dispatch('fleetTask/setSequence', seq)

      // Sync DO's between app state and sequence response
      const defaultSeq = seq.filter((item) => item.type !== 'START' && item.type !== 'END')
      const uniqueDeliveryOrders = Array.from(
        new Map(
          defaultSeq
            .flatMap((point) => point.activities)
            .filter((activity) => activity.delivery_order)
            .map((activity) => [activity.delivery_order.id, activity.delivery_order])
        ).values()
      )

      const dos = uniqueDeliveryOrders
      this.hasDeliveryOrder = dos.length > 0
      /**
       * To be better if expected_delivered_on and expected_pickup_on
       * attached in BE response
       */
      const augmentedDos = await this.augmentedDos(dos)
      await this.$store.dispatch('fleetTask/setSelectedDos', augmentedDos)

      if(this.isSamator){
        const loadRes = await FleetTaskPoint.getActLoads(this.$route.params.id)
        if (loadRes.errors) return
        
        this.sequenceActLoads = loadRes.data
      }
      this.scrollToMovedActivity()
    },
    async augmentedDos(dos) {
      const newDos = []

      for (const deliveryOrder of dos) {
        const data = deliveryOrder

        newDos.push({
          ...deliveryOrder,
          ...{
            customer: data.customer,
            delivery_order_id: data.id,
            expected_delivered_on: data.expected_delivered_on,
            expected_pickup_on: data.expected_pickup_on,
            is_from_vendor_request: data.is_from_vendor_request,
            external_reference_number: data.external_reference_number,
            reference_number: data.reference_number,
            shipment_number: data.shipment_number,
            origin_address: data.origin_address,
            destination_address: data.destination_address,
            shipment_type: data.shipment_type,
            transits: data.transits,
            load_count: data.load_count,
            is_load_adjusted: data.is_load_adjusted,
            status: data.status,
          },
        })
      }
      return newDos
    },
    async updateDetails(data) {
      if (this.isUpdatingVehicleAndDriver) {
        if (this.updateDriver != null) {
          data.driver_user_id = this.updateDriver
        }
        this.isUpdatingVehicleAndDriver = false
      }
      const [newValue, hasChanged] = this.filterUnchangedData(
        data,
        this.parseData(this.showResponse)
      )
      if (!hasChanged) return true

      const transformedData = { ...newValue }
      const numberFields = [
        'start_address_id',
        'end_address_id',
        'driver_user_id',
        'driver_user_2_id',
        'vehicle_id',
      ]

      numberFields.forEach((k) => {
        if (Object.prototype.hasOwnProperty.call(transformedData, k)) {
          const value = transformedData[k]
          transformedData[k] =
            value === null || value === undefined || value === '' ? null : parseInt(value, 10)
        }
      })

      if (
        Object.keys(transformedData).length === 1 &&
        Object.prototype.hasOwnProperty.call(transformedData, 'notification_enabled')
      ) {
        delete transformedData.vendor_id
        delete transformedData.user_group_id
      } else {
        if (!(this.fleetTaskStatus === 'Ditolak' && transformedData.vendor_id)) {
          transformedData.vendor_id = transformedData.vendor_id ?? null
        }
      }

      if (!this.isSendBySelf) {
        transformedData.expected_departure_on = null
        transformedData.shipment_reference = null
      }

      if (data.notification_enabled && data.customer_phone_number) {
        this.hasErrorNotification = { show: false, message: null }
      }

      if (transformedData.user_group_id != null) {
        transformedData.user_group_id = Number(transformedData.user_group_id)
      }

      if (transformedData.bin_num) {
        const selectedBinObject = this.fullObjects.bin_num
        transformedData.custom_field = {
          ...transformedData.custom_field,
          bin_num: transformedData.bin_num,
          part_num: selectedBinObject?.part_num,
          warehouse_code: selectedBinObject?.warehouse_code,
        }

        delete transformedData.bin_num
      }

      if (Object.prototype.hasOwnProperty.call(transformedData, 'trailer_id')) {
        // nilainya adalah string ketika sama, obj ketika bin num diubah
        const selectedBinObject = this.fullObjects.bin_num

        transformedData.custom_field = {
          ...transformedData.custom_field,
          trailer_id: transformedData.trailer_id || null,
          bin_num: data.bin_num,
          part_num: selectedBinObject?.part_num ?? this.responsePartNum,
          warehouse_code: selectedBinObject?.warehouse_code ?? this.responseWarehouseCode,
        }

        delete transformedData.trailer_id
      }

      if (this.hasFeatureFleetTaskContractMGM) {
        const fields = [
          'fleet_task_type',
          'contract_type',
          'vehicle_category_name',
          'vehicle_type_id',
        ]
        const customField = {}

        fields.forEach((field) => {
          if (Object.prototype.hasOwnProperty.call(transformedData, field)) {
            customField[field] = transformedData[field] ?? null
            delete transformedData[field]
          }
        })

        if (Object.keys(customField).length) {
          transformedData.custom_field = {
            ...(transformedData.custom_field || {}),
            ...customField,
          }
        }
      }

      const res = await FleetTask.update(this.$route.params.id, transformedData)
      if (res.errors) {
        let errorMessage = toastErrors['failed-to-save']

        if (res.errors_v2) {
          let errorsV2 = this.mapErrorsV2(res.errors_v2)
          const issueCodes = res.errors_v2.issues.map((issue) => issue.code)
          let foundErrorCode = false

          for (const code of issueCodes) {
            if (errorCodeInputMap[code]) {
              foundErrorCode = true
              errorMessage = errorCodeInputMap[code]

              if (code === 'FLEET_TASK.VEHICLE_GROUP_INVALID') {
                this.$set(this.codeErrors, 'vehicle_id', errorMessage)
                this.$set(this.formErrors, 'vehicle_id', this.$t(errorMessage))
              } else if (
                code === 'FLEET_TASK.DRIVER_USER_GROUP_INVALID' ||
                code === 'FLEET_TASK.DRIVER_USER_GROUP_EMPTY'
              ) {
                this.$set(this.codeErrors, 'driver_user_id', errorMessage)
                this.$set(this.formErrors, 'driver_user_id', this.$t(errorMessage))
              } else {
                Object.keys(errorsV2).forEach((fieldKey) => {
                  this.$set(this.codeErrors, fieldKey, errorMessage)
                  this.$set(this.formErrors, fieldKey, this.$t(errorMessage))
                })
              }
            }
          }

          if (foundErrorCode) {
            if (
              issueCodes.includes(
                'FLEET_TASK.FLEET_TASK_CUSTOMER_PHONE_NUMBER_INVALID',
                'FLEET_TASK.FLEET_TASK_CUSTOMER_PHONE_NUMBER_IS_REQUIRED'
              )
            ) {
              this.hasErrorNotification = { show: true, message: this.$t(errorMessage) }
              return false
            }

            // TODO: will be change later on the error message for different user group order
            if (
              issueCodes.includes('FLEET_TASK.FLEET_TASK.DELIVERY_ORDER_USER_GROUP_ID.DIFFERENT')
            ) {
              this.$set(this.formValues, 'user_group_id', null)
              this.$mcToast.error(errorMessage)
            }

            return false
          } else if (this.fleetTaskStatus === 'Dijadwalkan') {
            await this.$refs.formRef?.hasValidationErrors()
            this.$refs.formRef?.showErrors()
            this.isLoadingGlobal = true
            this.$mcToast.error(errorMessage)
            return false
          }
        }

        this.$mcToast.error(errorMessage)
        return false
      }

      if (!this.isCreating) {
        const defaultSettingsUpdate = {}
        if (this.saveAsDefaultStart && transformedData.start_address_id) {
          defaultSettingsUpdate.start_address_default = `${transformedData.start_address_id}`
        }
        if (this.saveAsDefaultEnd && transformedData.end_address_id) {
          defaultSettingsUpdate.end_address_default = `${transformedData.end_address_id}`
        }

        if (Object.keys(defaultSettingsUpdate).length > 0) {
          await this.updateDefaultAddressSettings(defaultSettingsUpdate)
        }
      }

      if (this.showToastAutomation.show) {
        this.$mcToast.success(this.showToastAutomation.message)
        this.showToastAutomation.show = false
      }

      await this.showData(this.$route.params.id)
      return true
    },
    async showData(id = null, cb) {
      this.autoStartFailed = {}
      const res = await FleetTask.show(id ?? this.$route.params.id)

      if (res?.status === 403 || res?.status === 404) {
        const data = {
          hasButton: true,
        }
        localStorage.setItem(res.status, JSON.stringify(data))
        this.$router.push({
          name: res.status,
        })
        return
      }

      if ('errors' in res && 'status' in res) {
        this.$mcToast.error(toastErrors['failed-to-get'])
        return
      }

      // if there's no status in response, it means the request succeeded
      // Map the error response to formErrors
      if ('errors' in res && res.status === undefined) {
        const fieldErrorKeys = Object.keys(res.errors)

        if (fieldErrorKeys.includes('customer_phone_number')) {
          this.hasErrorNotification = { show: true, message: this.$t('global.validation.required') }
        } else if (fieldErrorKeys.includes('auto_start_failed_reason')) {
          const reasons = res.errors['auto_start_failed_reason'] || []

          const reasonMap = {
            DRIVER_CONFLICT: {
              key: 'driver_user_id',
              message: this.$t('fleetTask.formV2.automation.fieldErrorDriverConflict'),
            },
            VEHICLE_CONFLICT: {
              key: 'vehicle_id',
              message: this.$t('fleetTask.formV2.automation.fieldErrorVehicleConflict'),
            },
          }

          reasons.forEach((reason) => {
            const map = reasonMap[reason]
            if (map) this.$set(this.formErrors, map.key, map.message)
          })

          if (reasons.includes('VEHICLE_CONFLICT') && reasons.includes('DRIVER_CONFLICT')) {
            this.autoStartFailed = {
              key: 'vehicle_driver_conflict',
              message: this.$t('fleetTask.formV2.automation.fieldErrorVehicleDriverConflict'),
            }
          } else {
            const firstReason = reasons.find((r) => reasonMap[r])
            if (firstReason) {
              const { key, message } = reasonMap[firstReason]
              this.autoStartFailed = { key, message }
            }
          }
        } else {
          fieldErrorKeys.forEach((fieldKey) => {
            if (
              [
                "driver doesn't have user group",
                'driver user group invalid',
                'vehicle group invalid',
              ].includes(res.errors?.[fieldKey]?.[0] && !fieldKey.includes('driver_user_2_id'))
            ) {
              const replacedKey = fieldKey.replace('_id', '')
              if (res.data?.[replacedKey]) {
                res.data[replacedKey] = null
              }

              this.$refs.formRef.showErrors()
              return
            }

            if (
              ['vehicle box type different', 'invalid value'].includes(res.errors?.[fieldKey]?.[0])
            ) {
              this.isBoxTypeError = true
              this.updateFleetTaskDetailFields()
            }

            const pureFieldKey = fieldKey.includes('.') ? fieldKey.split('.').pop() : fieldKey
            const errorCode = res.errors[fieldKey][0]
            const errorMessage = apiErrorMappings[pureFieldKey]?.[errorCode] ?? errorCode

            this.$set(this.formErrors, pureFieldKey, errorMessage)
          })
        }
      }

      if (res.data?.vehicle) {
        res.data.vehicle.hull_no = res.data.vehicle.hull_no
      }

      this.showResponse = res.data
      this.showErrors = res.errors

      const storeFleetTask = {
        id: res.data.id,
        box_type: res.data.box_type,
        status: res.data.status.name,
        expected_departure_on: res.data.expected_departure_on,
        shipment_reference: res.data.shipment_reference,
        start_address: res.data.start_address,
        end_address: res.data.end_address,
        auto_end_type: res.data.auto_end_type,
        vehicle: res.data.vehicle,
        hasDeliveryOrder: this.hasDeliveryOrder,
      }

      sessionStorage.setItem(`fleetTask_${res.data.id}`, JSON.stringify(storeFleetTask))

      this.$store.dispatch('fleetTask/setFleetTaskStatus', res.data.status.name)

      this.fleetTaskDetailFieldSource = cloneFieldConfigs(
        ['Draf', 'Dijadwalkan', 'Disetujui', ...(this.isSamator ? ['Dibatalkan'] : [])]
        .includes(res.data.status.name)
          ? draftFleetTaskDetailFields
          : temporaryFleetTaskDetailFields
      )
      if (res.data.status.name !== 'Dijadwalkan') {
        this.setVehicleSectionEditing(false)
      }

      this.boxType = res.data?.box_type?.name
      this.buffer = res.data?.buffer_time
      this.updateFleetTaskDetailFields()

      if (res.data.status.name == 'Ditolak') {
        this.sendBy = false
        this.hasVendor = true
        this.fullObjects.vendor_id = res.data?.vendor || null
      } else if (res.data?.vendor?.id) {
        this.sendBy = false
        this.hasVendor = true
      }

      await this.$nextTick()

      const newParsedData = this.parseData(res.data)

      Object.keys(newParsedData).forEach((key) => {
        if (this.focusedField !== key) {
          this.$set(this.formValues, key, newParsedData[key])
        }
      })
      this.vehicleTypeAutosaveGuardValue = this.formValues?.vehicle_type_id ?? null

      this.fleetOrderData = this.parseData(res.data)
      this.setAutocompleteSetting(pick(res.data, ['autocomplete_source', 'is_autocomplete_on']))

      this.isFromVendorRequest = res.data?.is_from_vendor_request
      this.$set(this.fullObjects, 'vendor_id', res.data?.vendor)
      this.$set(this.fullObjects, 'vehicle_id', res.data?.vehicle)
      this.$set(this.fullObjects, 'driver_user_id', res.data?.driver_user)
      this.$set(this.fullObjects, 'driver_user_2_id', res.data?.driver_user_2)
      this.endIfAllDoDone =
        (this.showResponse.auto_end_type === 'ACTIVITIES_COMPLETION' || (this.isCreating && this.hasDefaultEndFOByDOMGM)) ? true : false
      if (this.endIfAllDoDone) {
        this.disableDefaultEnd = true
      }
      this.isEstimatedOutsideOperationalTime =
        this.showResponse.is_estimated_outside_operational_time

      // bin num for samator
      if (this.hasBinNumberSamator) {
        const f = this.showResponse.custom_field
        this.scaleValue = f?.t1 != null || f?.t2 != null || f?.t3 != null
          ? ['t1', 't2', 't3']
              .filter((key) => f?.[key] != null)
              .map((key) => ({ key, value: f[key] }))
          : null
        this.$set(this.fullObjects, 'bin_num', this.showResponse.custom_field?.bin_num)
        this.isDisabledBinNum = false
        if (!this.showResponse.start_address?.id) {
          this.isDisabledBinNum = true
        }
      }

      if (this.hasFeatureFleetTaskContractMGM) {
        this.showResponse = {
          ...this.showResponse,
          fleet_task_type: this.showResponse?.custom_field?.fleet_task_type
            ? {
                id: this.showResponse.custom_field.fleet_task_type,
                name: this.showResponse.custom_field.fleet_task_type,
              }
            : null,
          contract_type: this.showResponse?.custom_field?.contract_type
            ? {
                id: this.showResponse.custom_field.contract_type,
                name: this.showResponse.custom_field.contract_type
                  .toLowerCase()
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (c) => c.toUpperCase()),
              }
            : null,
          vehicle_category_name: this.showResponse?.custom_field?.vehicle_category_name
            ? {
                category: this.showResponse.custom_field.vehicle_category_name,
                name: this.showResponse.custom_field.vehicle_category_name,
              }
            : null,
          vehicle_type_id: this.showResponse?.custom_field?.vehicle_type_id
            ? {
                id: this.showResponse.custom_field.vehicle_type_id,
                type: this.showResponse.custom_field.vehicle_type_name,
                name: this.showResponse.custom_field.vehicle_type_name,
              }
            : null,
        }

        this.$set(this.fullObjects, 'vehicle_type_id', this.showResponse.vehicle_type_id)

        if (this.showResponse.custom_field?.contract_type) {
          await this.fetchContractTypeDetails(this.showResponse.custom_field.contract_type)
        }

        // Set attachment files for FileUploaderCustom
        const firstAttachment = this.showResponse?.attachments?.[0]
        if (firstAttachment) {
          this.attachmentFiles = {
            id: firstAttachment.id,
            url: firstAttachment.file?.url,
            file_name: firstAttachment.file?.name || firstAttachment.file?.file_name,
          }
        } else {
          this.attachmentFiles = null
        }
      }

      await cb?.()
    },
    parseData(data) {
      this.responsePartNum = data?.custom_field?.part_num,
      this.responseWarehouseCode = data?.custom_field?.warehouse_code

      return {
        start_address_id: data?.start_address?.id,
        end_address_id: data?.end_address?.id,
        vehicle_id: data?.vehicle?.id,
        driver_user_id: data?.driver_user?.id,
        driver_user_2_id: data?.driver_user_2?.id,
        expected_departure_on: moment(data?.expected_departure_on).toISOString(true),
        vendor_id: data?.vendor?.id,
        shipment_reference: data?.shipment_reference,
        trailer_id: data?.custom_field?.trailer_id,
        notification_enabled: data?.notification_enabled,
        customer_phone_number: data?.customer?.phone_number,
        auto_start_type: data?.auto_start_type,
        auto_end_type: data?.auto_end_type,
        user_group_id: data?.user_group?.id,
        bin_num: data?.custom_field?.bin_num,
        fleet_task_type: data?.custom_field?.fleet_task_type,
        contract_type: data?.custom_field?.contract_type,
        vehicle_category_name: data?.custom_field?.vehicle_category_name,
        vehicle_type_id: data?.custom_field?.vehicle_type_id,
      }
    },
    scrollToMovedActivity() {
      if (!this.$route.hash) return

      const el =
        document.querySelector(`${this.$route.hash}`) ||
        document.querySelector('.mc-activity-table')
      if (!el) return

      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    },
    handleBack() {
      this.$refs['mc-modal-back'].show()
    },
    async handleValidateQuantity() {
      const res = await Samator.validateQuantity(this.$route.params.id)
      if(res?.errors){
        this.$mcToast.error(toastErrors['failed-to-validate-quantity']);
        return
      }
      
      this.$mcToast.success(this.$t('fleetTask.formV2.alerts.validationQuantitySucceed'));
      this.handleCancel()
    },
    handleCancel() {
      this.$store.dispatch('fleetTask/setSelectedDos', [])
      this.$store.dispatch('fleetTask/setSequence', [])

      let params = {}
      const query = { ...this.$route.query }
      const routeName = this.$route.query?.navsource || 'Fleet Task'
      delete query.navsource

      if (query.navsourceparam) {
        params = JSON.parse(query.navsourceparam)
      }

      this.$router.push({ name: routeName, params, query })
    },
    async showDetailData() {
      await Promise.all([this.showData(), this.getSequence()])
      await this.$store.dispatch('fleetTask/setIsLoadingGlobalDelay', false)
    },
    async handleTransitionStatus(data) {
      if (this.hasErrorNotification.show) {
        return
      }
      this.isLoadingTransition = true
      const transitionStatus = data?.transition?.status ? data.transition.status.id : 'DRAFT'
      const isDraftToScheduledTransition =
        this.fleetTaskStatus === 'Draf' && transitionStatus === 'SCHEDULED' && this.isSendBySelf

      switch (this.fleetTaskStatus) {
        case 'Temporary':
          this.track('Create Penugasan')
          break

        case 'Perjalanan':
          this.track('Selesaikan Penugasan')
          break

        case 'Draf':
          this.updateFleetTaskDetailFields({
            forceVehicleAndDriverRequired: isDraftToScheduledTransition,
          })
          await this.$refs.formRef.hasValidationErrors()
          if (this.hasFeatureFleetTaskContractMGM) {
            await this.$refs.attachmentForm?.hasValidationErrors()
          }
          break

        default:
          break
      }

      // Validate if its not a Fleet Task canceled transition
      const hasFormErrors = this.$refs.formRef.hasErrors
      const hasAttachmentErrors =
        this.hasFeatureFleetTaskContractMGM &&
        (['Draf', 'Menunggu Persetujuan'].includes(this.fleetTaskStatus) ||
          transitionStatus === 'WAITING_FOR_APPROVAL') &&
        this.$refs.attachmentForm?.hasErrors

      if (transitionStatus !== 'CANCELED' && (hasFormErrors || hasAttachmentErrors)) {
        this.$refs.formRef.showErrors()
        if (hasAttachmentErrors) {
          this.$refs.attachmentForm.showErrors()
        }
        this.$mcToast.error(toastErrors['invalid/invalid-fields'])
        this.isLoadingTransition = false
        return
      }

      if (this.setAutomation) {
        this.$bvModal.show('modal-confirm-set-automation')
        await this.$nextTick()
        this.setAutomation = false
        return
      }

      const mergedFields = {
        ...(data?.additionalPayload?.fields || {}),
        ...(data?.fields || {}),
      }

      const res = await Transition.transition(this.$route.params.id, {
        status: transitionStatus,
        ...data?.additionalPayload,
        ...(Object.keys(mergedFields).length ? { fields: mergedFields } : {}),
      })

      if (res.errors) {
        let toastMessage = toastErrors[transitionError.default]

        if (res.status === 400) {
          let errorsV2 = this.mapErrorsV2(res.errors_v2)
          const [rawFieldKey] = Object.keys(errorsV2)
          const fieldErrorKey = rawFieldKey?.replace(/^fields\./, '') || rawFieldKey
          const issueCodes = res.errors_v2.issues.map((issue) => issue.code)
          let foundErrorCode = false

          for (const code of issueCodes) {
            if (errorCodeToastMap[code]) {
              foundErrorCode = true
              toastMessage = errorCodeToastMap[code]
              if (code === 'FLEET_TASK.FLEET_TASK_PREVIOUS_ACTIVITY_NOT_COMPLETED') {
                const message = res.errors_v2.issues.find((issue) => issue.code === code)?.message
                const doMatch = message.match(/\bDO-\d+\b/)
                const foMatch = message.match(/\bFO-\d+\b/)

                const do_number = doMatch ? doMatch[0] : null
                const fo_number = foMatch ? foMatch[0] : null
                toastMessage = this.$t('fleetTask.formV2.text.vendorPreviousActivityNotCompleted', {
                  doNumber: do_number,
                  foNumber: fo_number,
                })
              } else if (code === 'FLEET_TASK.VEHICLE_BOX_TYPE_ID.DIFFERENT') {
                this.$set(this.formErrors, 'vehicle_id', this.$t(errorCodeInputMap[code]))
                this.isBoxTypeError = true
                this.updateFleetTaskDetailFields()
              }
              break
            } else if (
              code === 'FLEET_TASK.DRIVER_USER_GROUP_INVALID' ||
              code === 'FLEET_TASK.DRIVER_USER_GROUP_EMPTY'
            ) {
              if (fieldErrorKey === 'driver_user_id') {
                this.$set(this.codeErrors, 'driver_user_id', errorCodeInputMap[code])
                this.$set(this.formErrors, 'driver_user_id', this.$t(errorCodeInputMap[code]))
              } else if (fieldErrorKey === 'driver_user_2_id') {
                let mappedCode = code
                if (mappedCode === 'FLEET_TASK.DRIVER_USER_GROUP_INVALID') {
                  mappedCode = 'FLEET_TASK.DRIVER_USER_2_GROUP_INVALID'
                }

                this.$set(this.codeErrors, 'driver_user_2_id', errorCodeInputMap[mappedCode])
                this.$set(
                  this.formErrors,
                  'driver_user_2_id',
                  this.$t(errorCodeInputMap[mappedCode])
                )
              }
              foundErrorCode = true
            } else if (errorCodeInputMap[code]) {
              Object.keys(errorsV2).forEach((fieldKey) => {
                this.$set(this.codeErrors, fieldKey, errorCodeInputMap[code])
                this.$set(this.formErrors, fieldKey, this.$t(errorCodeInputMap[code]))
              })
            }
          }

          const hasDoTransitError = issueCodes.includes(
            'FLEET_TASK.FLEET_TASK_CANNOT_UNASSIGN_TRANSIT_DELIVERY_ORDER'
          )
          this.$store.dispatch('fleetTask/setHasDoTransitError', hasDoTransitError)

          if (toastErrors[transitionError[res.message]]) {
            toastMessage = toastErrors[transitionError[res.message]]
          } else if (errorsV2['status']?.message.includes('invalid type')) {
            toastMessage = toastErrors['invalid/invalid-value']

            if (
              errorsV2['status']?.expected === 'STARTED' &&
              errorsV2['status']?.received === 'ENDED'
            ) {
              toastMessage = this.$t('fleetTask.transitionErrors.statusHasBeenEnded')
            }
          } else if (res?.message?.includes('KMDI Anji API response error')) {
            toastMessage = res.message
          }

          if (!foundErrorCode) {
            const errorsWithoutMessages = Object.keys(res.errors).reduce((acc, key) => {
              acc[key] = ' '
              return acc
            }, {})
            this.formErrors = Object.assign({}, this.formErrors, errorsWithoutMessages)

            const fieldErrorKeys = Object.keys(res.errors)
            fieldErrorKeys.forEach((fieldKey) => {
              const errMessage = res.errors[fieldKey][0]
              const key = fieldKey.split('_')

              if (errMessage?.includes('already registered on')) {
                toastMessage = toastErrors[`invalid/${key[0]}-already-used`]
                this.$set(this.formErrors, fieldKey, apiErrorMappings[fieldKey]['already used'])
                return
              } else if (errMessage?.includes('invalid value')) {
                toastMessage = toastErrors[`invalid/${key[0]}-invalid-value`]
                this.$set(this.formErrors, fieldKey, apiErrorMappings[fieldKey]['invalid value'])
              } else if (this.hasFeatureFleetTaskContractMGM) {
                const pureFieldKey = fieldKey.includes('.') ? fieldKey.split('.').pop() : fieldKey
                const errorCode = res.errors[fieldKey][0]
                const errorMessage = apiErrorMappings[pureFieldKey]?.[errorCode] ?? errorCode
                this.$set(this.formErrors, pureFieldKey, errorMessage)
              }
            })
          }
        }

        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: toastMessage,
              },
            ],
          },
        })
        this.isLoadingTransition = false
        return
      }

      if (transitionStatus == 'DRAFT') {
        await this.showDetailData()
        this.saveDefaultAddressAfterCreating()
      }
      this.showSaveSuccessToast(data?.transition)

      this.isLoadingTransition = false

      if (transitionStatus !== 'DRAFT') {
        this.$router.push({
          name: 'Fleet Task',
        })
      }
    },
    showSaveSuccessToast(transition) {
      const clickListener = () => {
        this.$router.push({
          name: 'Fleet Task Detail',
          params: { id: this.showResponse?.id },
        })
      }

      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              text: `${this.showResponse?.number}`,
              class: '__link',
              listeners: { click: clickListener },
            },
            { text: this.$t('fleetTask.formV2.text.savedAs') },
            {
              tag: 'strong',
              text: `${
                transition?.status?.label
                  ? this.$t(transition.status.label)
                  : this.$t('fleetTask.status.draft')
              }`,
            },
          ],
        },
      })
    },
    handleAttachDelivery() {
      if (this.hasVendor) {
        this.$root.$emit('bv::show::modal', 'modal-change-seq-vendor', { fromAttachDelivery: true })
        return
      }
      const query = { ...this.$route.query }

      if (this.fleetTaskStatus === 'Perjalanan') {
        query.isIntervention = true
      }

      const params = {
        id: this.$route.params.id,
      }

      if (this.showResponse?.user_group) {
        params.user_group = this.showResponse.user_group
      }

      this.$router.push({
        name: 'Fleet Task Attach Delivery',
        params,
        query,
      })
    },
    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}

      keys.forEach((key) => {
        // if both value is falsy
        if (!oldValue[key] && !newValue[key]) return

        // if oldValue has same value with newValue
        if (oldValue[key] == newValue[key]) return

        // if the oldValue is 'Invalid date' and newValue is null
        if (oldValue[key] === 'Invalid date' && newValue[key] === null) return

        // if it is array
        if (Array.isArray(oldValue[key]) || Array.isArray(newValue[key])) {
          // if oldValue is undefined but the newValue is empty array
          if (!oldValue[key] && newValue[key].length === 0) return

          // matching oldValue and newValue array
          const sortedNewValue = newValue[key]?.length ? newValue[key].sort() : newValue[key]
          const sortedOldValue = oldValue[key] ? oldValue[key].sort() : oldValue[key]

          const isSameArray = isEqual(sortedNewValue, sortedOldValue)

          if (isSameArray) return
        }

        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    handleTrackFO() {
      this.dataFO = this.showResponse || {}
      this.$bvModal.show('modal-link')
    },
    setAutocompleteSetting(setting) {
      this.initialAutoCompleteSetting = setting
      this.autoCompleteSource =
        this.autoCompleteOptions.find((option) => option.value === setting.autocomplete_source) ??
        null
      this.isAutoCompleteOn = setting.is_autocomplete_on
    },
    async fetchContractTypeDetails(type) {
      try {
        const customer_id = this.showResponse?.customer?.id
        const res = await Contract.show(type, customer_id)
        if (res) {
          this.$set(this.fullObjects, 'contract_type', res?.data)
        }
      } catch (error) {
        console.error('Failed to fetch contract type details:', error)
      }
    },
    async updateAutocompleteSetting(data) {
      await this.$store.dispatch('fleetTask/setIsLoadingGlobalDelay', true)
      this.$store.dispatch('fleetTask/setIsInitial', false)
      const res = await FleetTask.update(this.$route.params.id, data)

      if (res.errors) return

      this.showData(null, async () => {
        await this.$store.dispatch('fleetTask/setIsLoadingGlobalDelay', false)
      })
    },
    async updateDefaultAddressSettings(settingKeyValues) {
      try {
        const payload = {}
        for (const [settingName, value] of Object.entries(settingKeyValues)) {
          if (settingName === 'start_address_toggle') {
            this.disableDefaultStart = true
          } else if (settingName === 'end_address_toggle') {
            this.disableDefaultEnd = true
          }
          payload[settingName] = value
        }
        await Setting.update(payload)
      } catch (e) {
        this.$mcToast.error(toastErrors['failed-save-settings'])
      } finally {
        for (const [settingName, _] of Object.entries(settingKeyValues)) {
          if (settingName === 'start_address_toggle') {
            this.disableDefaultStart = false
          } else if (settingName === 'end_address_toggle') {
            this.disableDefaultEnd = false
          }
        }
      }
    },

    updateFleetTaskDetailFields({ forceVehicleAndDriverRequired = false } = {}) {
      const removedVehicleFields = []
      const sourceFields = cloneFieldConfigs(this.fleetTaskDetailFieldSource)

      this.fleetTaskDetailFields = sourceFields
        .filter((field) => {
          if (!this.boxType && field.name === 'box_type') {
            return false
          }

          if (field.requiresLiquid && !this.isContainLiquid) {
            return false
          }

          if (this.hasFeatureFleetTaskContractMGM && this.isVehicleSectionField(field.name)) {
            removedVehicleFields.push({
              ...field,
              validation:
                (this.isLessTruckLoad && field.name === 'contract_type') ||
                (this.fleetTaskStatus === 'Draf' &&
                  ['vehicle_id', 'driver_user_id'].includes(field.name) &&
                  !forceVehicleAndDriverRequired)
                  ? ''
                  : field.validation,
            })
            return false
          }

          if (this.isLessTruckLoad && field.name === 'contract_type') {
            removedVehicleFields.push({
              ...field,
              validation: '',
            })
            return false
          }

          return true
        })
        .map((field) => {
          if (field.name === 'box_type') {
            return {
              ...field,
              result: this.boxType,
              isError: this.isBoxTypeError,
            }
          }

          if (
            this.hasFeatureFleetTaskContractMGM &&
            this.fleetTaskStatus === 'Draf' &&
            field.name === 'driver_user_id'
          ) {
            return {
              ...field,
              validation: '',
            }
          }

          return field
        })

      this.vehicleRelatedFields = removedVehicleFields
    },

    saveDefaultAddressAfterCreating() {
      const payload = {}
      if (this.saveAsDefaultStart && this.formValues.start_address_id) {
        payload.start_address_default = `${this.formValues.start_address_id}`
      }
      if (this.saveAsDefaultEnd && this.formValues.end_address_id) {
        payload.end_address_default = `${this.formValues.end_address_id}`
      }

      if (Object.keys(payload).length > 0) {
        this.updateDefaultAddressSettings(payload)
      }

      // Reset on page transition from Temporary to Draft
      this.saveAsDefaultStart = false
      this.saveAsDefaultEnd = false
    },

    mapErrorsV2(errors_v2) {
      const mappedErrors = {}

      if (errors_v2.issues && errors_v2.issues.length > 0) {
        errors_v2.issues.forEach((issue) => {
          let key
          if (issue?.path) {
            if (issue?.path.includes('address')) {
              key = issue?.path.slice(0, -1).join('.')
            } else {
              key = issue?.path.join('.')
            }

            mappedErrors[key] = {
              message: [issue.message],
              expected: issue?.expected,
              received: issue?.received,
            }
          }
        })
      }

      return mappedErrors
    },

    handleChangeVendor() {
      this.$set(this.formValues, 'vendor_id', null)
      this.sendBy = false
      this.hasVendor = false
    },

    handleTextInput(value, fieldName) {
      if (this.isLoadingInitial) return

      this.isDebouncing = true
      this.focusedField = fieldName

      this.$set(this.formValues, fieldName, value)

      this.debouncedUpdateFn()
    },

    applyTextUpdates() {
      this.isDebouncing = false
      this.focusedField = null
      this.updateDetailsAndGetSequence()
    },

    handleFieldBlur() {
      this.focusedField = null
    },

    handleNotificationChange(value) {
      this.formValues.notification_enabled = value
    },

    handleCustomerPhoneNumberChange(value) {
      this.handleTextInput(value, 'customer_phone_number')
    },
    async handleTravelAutomation() {
      if (this.fleetTaskStatus === 'Draf') {
        this.setAutomation = true
        const data = {
          transition: {
            status: {
              id: 'SCHEDULED',
              name: 'Dijadwalkan',
              label: 'fleetTask.status.scheduled',
            },
          },
          fields: {
            auto_start_type: 'GEOFENCE',
          },
        }
        this.handleTransitionStatus(data)
      } else {
        if (this.showResponse?.auto_start_type === 'GEOFENCE') {
          this.$bvModal.show('modal-deactivated-automation')
          return
        } else if (this.fleetTaskStatus === 'Dijadwalkan' && this.sameOriginAndFirstDoPickup) {
          this.$bvModal.show('modal-confirm-set-automation')
          return
        } else {
          this.formValues.auto_start_type = 'GEOFENCE'
          this.showToastAutomation = {
            show: true,
            message: this.$t('fleetTask.formV2.automation.enabledSuccess'),
            type: 'success',
          }
          this.updateDetailsAndGetSequence()
        }
      }
    },
    handleSetAutomation() {
      this.setAutomation = false
      if (this.fleetTaskStatus === 'Draf') {
        const data = {
          transition: {
            status: {
              id: 'SCHEDULED',
              name: 'Dijadwalkan',
              label: 'fleetTask.status.scheduled',
            },
          },
          fields: {
            auto_start_type: 'GEOFENCE',
          },
        }
        this.handleTransitionStatus(data)
      } else {
        this.formValues.auto_start_type = 'GEOFENCE'
        this.showToastAutomation = {
          show: true,
          message: this.$t('fleetTask.formV2.automation.enabledSuccess'),
          type: 'success',
        }
        this.updateDetailsAndGetSequence()
      }
    },
    handleDeactivatedtAutomation() {
      this.formValues.auto_start_type = null
      this.showToastAutomation = {
        show: true,
        message: this.$t('fleetTask.formV2.automation.disabledSuccess'),
        type: 'success',
      }
      this.updateDetailsAndGetSequence()
    },

    handleFullObject({ key, value }) {
      const vehicleDriverId = value?.driver?.id
      const isDriverAvailable = value?.driver?.is_available !== false

      if (key === 'vehicle_id' && vehicleDriverId !== this.formValues.driver_user_2_id) {
        this.isUpdatingVehicleAndDriver = true
      }

      if (key === 'vehicle_id' && isDriverAvailable) {
        this.updateDriver = vehicleDriverId || null
      } else if (key === 'driver_user_id') {
        this.updateDriver = value?.id || null
      } else {
        this.updateDriver = null
      }

      if (this.fullObjects && key) {
        this.$set(this.fullObjects, key, value)
      }

      if (key === 'contract_type' && value?.id) {
        this.fetchContractTypeDetails(value.id)
      }
    },
    handleChangeEndIfAllDoDone(event) {
      this.endIfAllDoDone = event.target.checked
    },
    updateDetailFO() {
      const exFO = JSON.parse(sessionStorage.getItem(`fleetTask_${this.$route.query.exFO}`))
      const updateData = {
        expected_departure_on: exFO.expected_departure_on,
        start_address_id: exFO.start_address.id,
        end_address_id: exFO.end_address.id,
        auto_end_type: exFO.auto_end_type,
      }
      this.updateDetails(updateData)
    },
    handleBulkTransfer() {
      this.$bvModal.show('mc-modal-bulk-transfer')
    },
    canTransferDeliveryOrder(deliveryOrder) {
      // Cannot transfer vendor requests
      if (deliveryOrder?.is_from_vendor_request) {
        return false
      }

      // Cannot transfer TRANSIT shipment types
      if (deliveryOrder?.shipment_type === 'TRANSIT') {
        return false
      }

      const validDeliveryOrderStatus = [
        'Dikonfirmasi',
        'Dijadwalkan',
        'Dijalankan',
        'Diambil',
        'Dijadwalkan Ulang',
      ]
      if (!validDeliveryOrderStatus.includes(deliveryOrder?.status?.name)) {
        return false
      }

      return true
    },
    async handleFileChange(fileData) {
      try {
        const fleetTaskId = this.$route.params.id
        if (!fleetTaskId) return

        const formData = new FormData()
        formData.append('files', fileData.file)

        await Mgm.upload(fleetTaskId, formData)

        this.$mcToast.success(this.$t('fleetTask.formV2.attachment.uploadSuccess'))
        await this.showDetailData()
      } catch (error) {
        console.error('Error uploading file:', error)
        this.$mcToast.error(this.$t('fleetTask.formV2.attachment.uploadFailed'))
      }
    },
    async handleFileRemoved(fileData) {
      try {
        const fleetTaskId = this.$route.params.id
        if (!fleetTaskId || !fileData?.id) return

        await Mgm.remove(fleetTaskId, fileData.id)

        this.$mcToast.success(this.$t('fleetTask.formV2.attachment.removeSuccess'))
        await this.showDetailData()
      } catch (error) {
        console.error('Error removing file:', error)
        this.$mcToast.error(this.$t('fleetTask.formV2.attachment.removeFailed'))
      }
    },
    toHour(date) {
      return moment(date).format('HH:mm')
    },
    handleDownloadPhoto() {
      this.$bvModal.show('modal-download-photo')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './form.scss';
</style>
